


import { Injectable } from '@angular/core';
import { HttpEvent, HttpRequest, HttpResponse } from '@angular/common/http';
import { delay, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { ApiResponse } from '../models/user.model';
import { ConnectionType } from '../models/connection.model';
import { TENANT_SCHEMAS } from '../demo/tenant-a-schemas';
import type { TenantSchema } from '../demo/tenant-a-schemas';
import type { AggregationType, ToleranceType, JobRunStatus } from '@datarecs/data-models-core-api';
import type { EmailDetailLevel } from '@datarecs/data-models-core-api';
import type { TenantEmailSettings } from '@datarecs/data-models-core-api';
import type { AuditLogEntry } from '@datarecs/data-models-core-api';
import type { AuditEventEntry, AuditExportJob } from '@datarecs/data-models-core-api';
import type { RoleBindingSubjectTypes, RoleBindingRoleTypes, RoleBindingScopes } from '@datarecs/data-models-core-api';
import type { EmailRecipientRuleType } from '@datarecs/data-models-core-api';
import type { SupportMessage } from '@datarecs/data-models-core-api';
import type { Job } from '../models/job.model';

import type { DemoPermission } from '../models/demo-permission.model';
import { DEMO_PERMISSIONS } from './demo-permissions.generated';

interface DemoColumn {
  name: string;
  type: string;
  nullable: boolean;
  primary_key: boolean;
}

interface DemoTable {
  name: string;
  columns: DemoColumn[];
}

interface DemoSchema {
  name: string;
  tables: DemoTable[];
}

interface DemoDatabase {
  name: string;
  schemas: DemoSchema[];
}

interface DemoConnectionDefinition {
  summary: any;
  metadata: DemoDatabase[];
}

interface DemoJobRun {
  id: string;
  request_id: string;
  job_id: string;
  workspace_id: string;
  status: DemoRunStatus;
  triggered_by: string;
  run_label?: string;
  argo_workflow_name?: string;
  started_at?: string;
  completed_at?: string;
  rows_processed: number;
  rows_matched: number;
  rows_unmatched: number;
  error?: { code: string; message: string; details?: string };
  parameters?: Record<string, string>;
}

interface DemoJobEvent {
  id: string;
  job_id: string;
  run_id: string;
  event_type: string;
  payload: Record<string, any>;
  created_at: string;
}

interface DemoJobSchedule {
  schedule_id: string;
  job_id: string;
  cron_expression: string;
  timezone?: string;
  enabled: boolean;
  params?: Record<string, string>;
  next_run_at?: string;
  last_run_at?: string;
  created_at: string;
  updated_at?: string;
}

// Mirrors the real JobRunStatus enum exactly (QUEUED, RUNNING, COMPLETED, ERRORED, CANCELLED) —
// note ERRORED, not FAILED. data-models-core-api ships a regression test asserting FAILED is
// NOT a member; demo data using 'FAILED' here previously fell through every status-badge
// switch's explicit ERRORED case into the default/unknown fallback everywhere it's rendered.
type DemoRunStatus = 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'ERRORED' | 'CANCELLED';

interface DemoUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: string[];
  permissions: string[];
  status: 'active' | 'pending' | 'disabled';
  last_login_at?: string;
  invited_at?: string;
}

interface DemoInvitation {
  id: string;
  email: string;
  created_at: string;
  status: 'pending' | 'accepted' | 'cancelled';
  user_id: string;
  auto_accept_at?: string;
}

interface DemoJoinRequest {
  id: string;
  email: string;
  created_at: string;
  status: 'pending' | 'approved' | 'denied';
}

interface DemoEncryptionKeyResponse {
  kek: {
    name: string;
    ownership: 'managed' | 'byok';
    residence: string;
    last_rotated: string;
    status: string;
  };
  deks: Array<{
    name: string;
    purpose: string;
    last_rotated: string;
    rotation_frequency: string;
  }>;
}

interface DemoGroup {
  id: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

interface DemoGroupMember {
  user_id: string;
  group_id: string;
  first_name: string;
  last_name: string;
  email: string;
  joined_at: string;
}

interface DemoSupportConversation {
  id: string;
  subject: string;
  status: string;
  created_at: string;
  /**
   * Demo-only bookkeeping (not part of the real `SupportConversation` contract, never
   * serialized directly) — the last time the demo user viewed this conversation's detail.
   * Stands in for Chatwoot's contact-last-seen; drives the computed `unread_count` below.
   */
  contactLastViewedAt: string;
}

interface DemoSupportMessage {
  id: string;
  conversation_id: string;
  body: string;
  author_type: 'contact' | 'agent';
  author_name: string;
  /**
   * ISO timestamp. May be in the FUTURE relative to `Date.now()` — used to schedule a
   * canned agent auto-reply a fixed delay after a customer message, so the reply-loop
   * poll has something real to pick up in demo mode. `visibleSupportMessages()` filters
   * these out until "now" (real or Playwright-virtual-clock-driven) reaches them.
   */
  created_at: string;
}

/** How long after a customer reply the demo's canned agent auto-reply becomes visible. Matches the real thread poll interval (`SUPPORT_THREAD_POLL_MS`) so one poll tick is enough to pick it up in an E2E test using a virtual clock. */
const DEMO_SUPPORT_AGENT_REPLY_DELAY_MS = 30_000;

const ALL_PERMISSION_NAMES = DEMO_PERMISSIONS.map((p) => p.name);

const TENANT_PERMISSION_NAMES = DEMO_PERMISSIONS.filter((p) => p.scope === 'tenant').map((p) => p.name);
const WORKSPACE_PERMISSION_NAMES = DEMO_PERMISSIONS.filter((p) => p.scope === 'workspace').map((p) => p.name);
const ADMIN_PERMISSION_NAMES = Array.from(new Set([...TENANT_PERMISSION_NAMES, ...WORKSPACE_PERMISSION_NAMES]));
const SECURITY_PERMISSION_NAMES = DEMO_PERMISSIONS.filter((p) => ['config', 'access_management'].includes(p.category)).map((p) => p.name);
const PRODUCT_PERMISSION_NAMES = DEMO_PERMISSIONS.filter((p) => ['jobs', 'connections', 'workspaces'].includes(p.category)).map((p) => p.name);
const DATA_PERMISSION_NAMES = DEMO_PERMISSIONS.filter((p) => ['jobs', 'connections'].includes(p.category)).map((p) => p.name);
const VIEWER_PERMISSION_NAMES = DEMO_PERMISSIONS.filter((p) => ['jobs', 'connections'].includes(p.category) && ['list', 'view', 'export'].includes(p.action)).map((p) => p.name);
const INVITE_AUTO_ACCEPT_MS = 40_000;


type CreateJobPayload = Pick<Job, 'name' | 'workspace_id' | 'dimensions' | 'measures' | 'connections' | 'stages'>;

const DAY_IN_MS = 24 * 60 * 60 * 1000;

function daysAgoIso(days: number): string {
  return new Date(Date.now() - days * DAY_IN_MS).toISOString();
}

function minutesAgoIso(minutes: number): string {
  return new Date(Date.now() - minutes * 60 * 1000).toISOString();
}

function daysAgoDate(days: number): Date {
  return new Date(Date.now() - days * DAY_IN_MS);
}

interface ConnectionBlueprint {
  summary: any;
  database: string;
}

interface JobSeedStage {
  dimensions: string[];
  tolerances: { measure: string; type: ToleranceType; value: number }[];
}

interface JobSeed {
  id: string;
  name: string;
  description: string;
  workspace_id: string;
  job_group_id: string;
  schema: string;
  table: string;
  connections: string[];
  dimensions: string[];
  measures: { alias: string; column: string; aggregation: AggregationType }[];
  stages: JobSeedStage[];
}

const ENGINE_DEFS: Array<{
  key: string;
  label: string;
  type: ConnectionType;
  database: string;
  descriptionTemplate: (ws: string) => string;
}> = [
  { key: 'postgres', label: 'PostgreSQL', type: ConnectionType.POSTGRES, database: 'tenant_a_postgres', descriptionTemplate: (ws) => `Primary PostgreSQL replica for ${ws} data.` },
  { key: 'mysql', label: 'MySQL', type: ConnectionType.MYSQL, database: 'tenant_a_mysql', descriptionTemplate: (ws) => `MySQL snapshot database for ${ws} workloads.` },
  { key: 'mssql', label: 'SQL Server', type: ConnectionType.MSSQL, database: 'tenant_a_mssql', descriptionTemplate: (ws) => `SQL Server warehouse for ${ws} reporting.` },
  { key: 'db2', label: 'DB2', type: ConnectionType.DB2, database: 'tenant_a_db2', descriptionTemplate: (ws) => `DB2 mainframe extract for ${ws} reconciliation.` },
  { key: 'oracle', label: 'Oracle', type: ConnectionType.ORACLE, database: 'tenant_a_oracle', descriptionTemplate: (ws) => `Oracle ERP datasets for ${ws} operations.` },
];

const WORKSPACE_DEFS: Array<{ id: string; short: string; key: string }> = [
  { id: 'ws-ecommerce', short: 'E-Commerce', key: 'ecommerce' },
  { id: 'ws-marketing', short: 'Marketing', key: 'marketing' },
  { id: 'ws-analytics', short: 'Analytics', key: 'analytics' },
];

function buildConnectionBlueprints(): ConnectionBlueprint[] {
  const blueprints: ConnectionBlueprint[] = [];
  let versionCounter = 1;
  let dayOffset = 0;

  for (const ws of WORKSPACE_DEFS) {
    for (const engine of ENGINE_DEFS) {
      const connId = `conn-${engine.key}-${ws.key}`;
      const createdDaysAgo = 150 - dayOffset * 8;
      const updatedDaysAgo = Math.max(1, 15 - dayOffset);
      const lastUsedDaysAgo = dayOffset % 5;

      const statusByOffset: Record<number, string> = { 3: 'error', 7: 'inactive', 11: 'error' };
      blueprints.push({
        summary: {
          id: connId,
          name: `${engine.label} (${ws.short})`,
          description: engine.descriptionTemplate(ws.short),
          type: engine.type,
          status: statusByOffset[dayOffset] || 'active',
          workspace_id: ws.id,
          last_used_at: daysAgoIso(lastUsedDaysAgo),
          version: versionCounter++,
          created_at: daysAgoIso(createdDaysAgo),
          updated_at: daysAgoIso(updatedDaysAgo),
          created_by: 'demo-user',
          updated_by: 'demo-user'
        },
        database: engine.database
      });
      dayOffset++;
    }
  }
  return blueprints;
}

const CONNECTION_BLUEPRINTS: ConnectionBlueprint[] = buildConnectionBlueprints();

const CONNECTION_NAME_MAP = new Map(CONNECTION_BLUEPRINTS.map((blueprint) => [blueprint.summary.id, blueprint.summary.name]));

function cloneSchemas(): DemoSchema[] {
  return TENANT_SCHEMAS.map((schema) => ({
    name: schema.schema,
    tables: schema.tables.map((table) => ({
      name: table.name,
      columns: table.columns.map((column) => ({ ...column }))
    }))
  }));
}

function buildDemoConnections(): DemoConnectionDefinition[] {
  return CONNECTION_BLUEPRINTS.map((blueprint) => ({
    summary: blueprint.summary,
    metadata: [
      {
        name: blueprint.database,
        schemas: cloneSchemas()
      }
    ]
  }));
}

function connectionLabelFor(id: string): string {
  return CONNECTION_NAME_MAP.get(id) || id;
}

function dedupePermissions(...lists: string[][]): string[] {
  return Array.from(new Set(lists.flat()));
}

function buildDemoUsers(): DemoUser[] {
  const now = Date.now();
  const lastLoginIso = (minutesAgo: number) => new Date(now - minutesAgo * 60 * 1000).toISOString();

  return [
    {
      id: 'user-mark',
      email: 'mark@datarecs.io',
      first_name: 'Mark',
      last_name: 'McCracken',
      roles: ['owner'],
      permissions: ALL_PERMISSION_NAMES,
      status: 'active',
      last_login_at: lastLoginIso(90)
    },
    {
      id: 'user-niamh',
      email: 'niamh@datarecs.io',
      first_name: 'Niamh',
      last_name: 'Wilson',
      roles: ['admin'],
      permissions: ADMIN_PERMISSION_NAMES,
      status: 'active',
      last_login_at: lastLoginIso(45)
    },
    {
      id: 'user-brent',
      email: 'brent@datarecs.io',
      first_name: 'Brent',
      last_name: 'Hobbs',
      roles: ['admin'],
      permissions: dedupePermissions(SECURITY_PERMISSION_NAMES, ['view_job', 'view_job_results', 'list_jobs']),
      status: 'active',
      last_login_at: lastLoginIso(25)
    },
    {
      id: 'user-jez',
      email: 'jez@datarecs.io',
      first_name: 'Jez',
      last_name: 'Walker',
      roles: ['editor'],
      permissions: PRODUCT_PERMISSION_NAMES,
      status: 'active',
      last_login_at: lastLoginIso(180)
    },
    {
      id: 'user-simon',
      email: 'simon@datarecs.io',
      first_name: 'Simon',
      last_name: 'Dale',
      roles: ['workspace_user'],
      permissions: DATA_PERMISSION_NAMES,
      status: 'active',
      last_login_at: lastLoginIso(60)
    },
    {
      id: 'user-kate',
      email: 'kate@datarecs.io',
      first_name: 'Kate',
      last_name: 'Lewis',
      roles: ['viewer'],
      permissions: VIEWER_PERMISSION_NAMES,
      status: 'active',
      last_login_at: lastLoginIso(15)
    }
  ];
}

@Injectable({ providedIn: 'root' })
export class DemoDataService {
  private readonly latency = 200;

  private readonly demoUser: any = {
    id: 'demo-user-0001',
    workos_user_id: 'demo-workos',
    email: 'niamh.demo@datarecs.local',
    first_name: 'Niamh',
    last_name: 'Demo',
    metadata: { title: 'Frontend Lead' },
    roles: ['admin'],
    identityType: 0 as any,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    created_by: '00000000-0000-0000-0000-000000000000' as any,
    updated_by: '00000000-0000-0000-0000-000000000000' as any,
    version: 1
  };

  public readonly demoTenant: any = {
    id: 'demo-tenant-0001',
    display_name: 'Demo Tenant',
    settings: {
      email: {
        job_reports_enabled: true,
        allow_data_details: true,
        allow_workspace_override: true,
        template_logo_url: '',
        template_footer_text: '',
        template_legal_disclaimer: ''
      }
    }
  };

  private readonly workspaces: any[] = [
    {
      id: 'ws-ecommerce' as any,
      name: 'E-Commerce Operations',
      slug: 'e-commerce-operations',
      description: 'Reconciliation workspace for e-commerce transaction data, inventory, and order management systems',
      settings: {
        email: {
          job_reports_enabled: true,
          allow_data_details: true
        }
      },
      metadata: { region: 'us-east-1', team: 'commerce-platform' },
      created_at: daysAgoIso(120),
      updated_at: daysAgoIso(2),
      created_by: '00000000-0000-0000-0000-000000000000' as any,
      updated_by: '00000000-0000-0000-0000-000000000000' as any,
      version: 1
    },
    {
      id: 'ws-marketing' as any,
      name: 'Marketing & Advertising',
      slug: 'marketing-advertising',
      description: 'Reconciliation workspace for marketing spend, campaign performance, and advertising platform data',
      settings: {
        email: {
          job_reports_enabled: true,
          allow_data_details: true
        }
      },
      metadata: { region: 'us-east-1', team: 'growth-marketing' },
      created_at: daysAgoIso(90),
      updated_at: daysAgoIso(5),
      created_by: '00000000-0000-0000-0000-000000000000' as any,
      updated_by: '00000000-0000-0000-0000-000000000000' as any,
      version: 1
    },
    {
      id: 'ws-analytics' as any,
      name: 'Analytics & Reporting',
      slug: 'analytics-reporting',
      description: 'Reconciliation workspace for analytics pipelines, reporting data, and business intelligence systems',
      settings: {
        email: {
          job_reports_enabled: false,
          allow_data_details: true
        }
      },
      metadata: { region: 'us-east-1', team: 'data-engineering' },
      created_at: daysAgoIso(60),
      updated_at: daysAgoIso(1),
      created_by: '00000000-0000-0000-0000-000000000000' as any,
      updated_by: '00000000-0000-0000-0000-000000000000' as any,
      version: 1
    }
  ];
  private readonly connections: DemoConnectionDefinition[] = buildDemoConnections();
  /**
   * connection-health-check demo state: `conn-db2-ecommerce` starts life stale (its automated
   * health-check has repeatedly failed on invalid credentials) so the connection-detail health
   * tab can show the stale banner + real health-check strip + "Resume paused schedules" flow.
   * Hitting POST /connections/:id/resume-paused-schedules clears the stale flag so a reload shows
   * the recovered state — this Set records connections the demo user has recovered.
   */
  private readonly DEMO_STALE_CONNECTION_ID = 'conn-db2-ecommerce';
  private readonly recoveredStaleConnections = new Set<string>();
  private readonly jobGroups: any[] = [
    {
      id: 'jg-ecommerce-integrity' as any,
      name: 'E-Commerce Data Integrity',
      description: 'Validates order, customer, and product data across warehouse systems.',
      workspace_id: 'ws-ecommerce' as any,
      jobs: [],
      job_ids: [],
      created_at: daysAgoIso(60),
      updated_at: daysAgoIso(2),
      created_by: 'demo-user' as any,
      updated_by: 'demo-user' as any,
      version: 1
    },
    {
      id: 'jg-marketing-spend' as any,
      name: 'Marketing Spend Validation',
      description: 'Validates advertising spend and campaign metrics across replicated sources.',
      workspace_id: 'ws-marketing' as any,
      jobs: [],
      job_ids: [],
      created_at: daysAgoIso(45),
      updated_at: daysAgoIso(3),
      created_by: 'demo-user' as any,
      updated_by: 'demo-user' as any,
      version: 1
    },
    {
      id: 'jg-analytics-pipeline' as any,
      name: 'Analytics Pipeline Checks',
      description: 'Ensures analytics and reporting data is consistent across pipeline stages.',
      workspace_id: 'ws-analytics' as any,
      jobs: [],
      job_ids: [],
      created_at: daysAgoIso(30),
      updated_at: daysAgoIso(1),
      created_by: 'demo-user' as any,
      updated_by: 'demo-user' as any,
      version: 1
    }
  ];


  private readonly jobs: any[] = [];
  private readonly jobRuns = new Map<string, DemoJobRun[]>();
  private readonly jobRunEvents = new Map<string, DemoJobEvent[]>();
  private readonly jobSchedules = new Map<string, DemoJobSchedule[]>();
  private readonly apiKeys = [
    {
      id: 'key-ledger',
      name: 'Ledger ingestion',
      description: 'Used by nightly ledger sync tasks',
      metadata: { owner: 'platform' },
      prefix: 'drk_live_led',
      scopes: ['tenant:read'],
      permissions: ['list_jobs', 'view_job', 'create_job'],
      expiry_time: new Date(Date.now() + 1000 * 60 * 60 * 24 * 180).toISOString(),
      last_used_at: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
      created_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 45).toISOString(),
      updated_at: new Date().toISOString(),
      created_by: 'demo-user',
      updated_by: 'demo-user',
      version: 1
    },
    {
      id: 'key-analytics',
      name: 'Analytics API key',
      description: 'Connects Data Studio dashboards',
      metadata: { owner: 'analytics' },
      prefix: 'drk_live_anl',
      scopes: ['tenant:read'],
      permissions: ['list_job_groups', 'view_job_group', 'list_connections'],
      expiry_time: undefined,
      last_used_at: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
      created_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 10).toISOString(),
      updated_at: new Date().toISOString(),
      created_by: 'demo-user',
      updated_by: 'demo-user',
      version: 1
    }
  ];

  private readonly users: DemoUser[] = buildDemoUsers();

  private readonly invitations: DemoInvitation[] = [];
  private readonly invitationTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly joinRequests: DemoJoinRequest[] = this.buildJoinRequests();

  private readonly groups: DemoGroup[] = this.buildInitialGroups();
  private readonly groupMembers = this.buildInitialGroupMembers();

  private readonly platformRoles = [
    { id: 1, name: 'owner', scope: 'tenant', description: 'Highest permission level. Full access to all tenant resources.', permissions: ALL_PERMISSION_NAMES },
    { id: 2, name: 'admin', scope: 'tenant', description: 'Administrative access (no billing).', permissions: ADMIN_PERMISSION_NAMES },
    { id: 3, name: 'editor', scope: 'tenant', description: 'Create and manage workspace resources.', permissions: PRODUCT_PERMISSION_NAMES },
    { id: 4, name: 'viewer', scope: 'tenant', description: 'View-only access.', permissions: VIEWER_PERMISSION_NAMES },
    { id: 5, name: 'workspace_admin', scope: 'workspace', description: 'Manage everything inside a workspace.', permissions: WORKSPACE_PERMISSION_NAMES },
    { id: 6, name: 'workspace_user', scope: 'workspace', description: 'Create/edit workspace resources.', permissions: DATA_PERMISSION_NAMES },
    { id: 7, name: 'workspace_viewer', scope: 'workspace', description: 'View-only workspace access.', permissions: VIEWER_PERMISSION_NAMES }
  ];

  private readonly customRoles = [
    {
      id: 1001,
      name: 'data_steward',
      scope: 'tenant',
      description: 'Manages data quality and reconciliation jobs',
      permissions: ['list_jobs', 'view_job', 'run_job', 'list_job_groups', 'view_job_group']
    }
  ];

  private readonly permissions = DEMO_PERMISSIONS;

  private readonly roleAssignments = new Map<string, any[]>([
    ['owner', [
      { subject: 'demo-user-0001', subject_type: 'user', assigned_at: new Date().toISOString() },
      { subject: 'user-mark', subject_type: 'user', assigned_at: new Date().toISOString() },
      { subject: 'group-admin', subject_type: 'group', group_name: 'Administrators', assigned_at: new Date().toISOString() }
    ]],
    ['admin', [
      { subject: 'user-wilson', subject_type: 'user', assigned_at: new Date().toISOString() },
      { subject: 'user-brent', subject_type: 'user', assigned_at: new Date().toISOString() }
    ]],
    ['editor', [
      { subject: 'user-jez', subject_type: 'user', assigned_at: new Date().toISOString() }
    ]]
  ]);

  private readonly webhookEndpoints: any[] = [
    {
      id: 'ep-pagerduty',
      url: 'https://events.pagerduty.com/v2/enqueue',
      description: 'PagerDuty integration — reliable incident creation endpoint',
      enabled: true,
      rate_limit_rps: 10,
      signing_secret_rotated_at: daysAgoIso(10),
      consecutive_failures: 0,
      created_at: daysAgoIso(60),
      updated_at: daysAgoIso(1),
    },
    {
      id: 'ep-slack-flakey',
      url: 'https://hooks.slack.com/services/T00000/B00000/XXXX',
      description: 'Slack webhook — flakey, ~50% failure rate under load',
      enabled: true,
      rate_limit_rps: 5,
      signing_secret_rotated_at: null,
      consecutive_failures: 3,
      created_at: daysAgoIso(45),
      updated_at: daysAgoIso(3),
    },
    {
      id: 'ep-analytics',
      url: 'https://analytics.internal.datarecs.io/ingest/webhook',
      description: 'Analytics pipeline webhook — slow, 5-15s processing delay',
      enabled: true,
      rate_limit_rps: 2,
      signing_secret_rotated_at: null,
      consecutive_failures: 0,
      created_at: daysAgoIso(30),
      updated_at: daysAgoIso(5),
    },
    {
      // Auto-disabled: a dead target that crossed the 50-failure threshold. Drives the
      // prominent auto-disabled banner + re-enable affordance on the endpoint detail page.
      id: 'ep-billing-dead',
      url: 'https://billing.partner.example.com/hooks/datarecs',
      description: 'Partner billing webhook — host stopped responding (auto-disabled)',
      enabled: false,
      rate_limit_rps: 5,
      signing_secret_rotated_at: null,
      consecutive_failures: 50,
      created_at: daysAgoIso(90),
      updated_at: daysAgoIso(2),
    }
  ];

  private readonly webhookSubscriptions: any[] = [
    {
      id: 'sub-pd-failures',
      endpoint_id: 'ep-pagerduty',
      event_types: ['io.datarecs.reconciliation.run.failed'],
      filters: { workspace_id: 'ws-ecommerce' },
      enabled: true,
      created_at: daysAgoIso(60),
      updated_at: daysAgoIso(1),
    },
    {
      id: 'sub-slack-all-runs',
      endpoint_id: 'ep-slack-flakey',
      event_types: ['io.datarecs.reconciliation.run.completed', 'io.datarecs.reconciliation.run.failed'],
      filters: {},
      enabled: true,
      created_at: daysAgoIso(45),
      updated_at: daysAgoIso(3),
    },
    {
      id: 'sub-analytics-all',
      endpoint_id: 'ep-analytics',
      event_types: ['*'],
      filters: {},
      enabled: true,
      created_at: daysAgoIso(30),
      updated_at: daysAgoIso(5),
    }
  ];

  /**
   * Demo webhook deliveries — one row per attempted dispatch. The flakey Slack endpoint and the
   * dead billing endpoint carry FAILED rows so the delivery log, per-attempt history, single retry,
   * and bulk-replay affordances are all exercisable in demo mode.
   */
  private readonly webhookDeliveries: any[] = [
    {
      id: 'wd-001', endpoint_id: 'ep-pagerduty', subscription_id: 'sub-pd-failures',
      status: 'DELIVERED', event_type: 'io.datarecs.reconciliation.run.failed', event_id: 'evt-7f3a9c12',
      attempt_count: 1, last_response_code: 202, last_error: null, payload: '{}',
      created_at: minutesAgoIso(18), updated_at: minutesAgoIso(18),
    },
    {
      id: 'wd-002', endpoint_id: 'ep-slack-flakey', subscription_id: 'sub-slack-all-runs',
      status: 'FAILED', event_type: 'io.datarecs.reconciliation.run.completed', event_id: 'evt-1b9d4e80',
      attempt_count: 3, last_response_code: 429, last_error: 'HTTP 429 Too Many Requests (rate limited by Slack)',
      payload: '{}', created_at: minutesAgoIso(42), updated_at: minutesAgoIso(35),
    },
    {
      id: 'wd-003', endpoint_id: 'ep-slack-flakey', subscription_id: 'sub-slack-all-runs',
      status: 'DELIVERED', event_type: 'io.datarecs.reconciliation.run.failed', event_id: 'evt-55c0aa31',
      attempt_count: 2, last_response_code: 200, last_error: null, payload: '{}',
      created_at: minutesAgoIso(70), updated_at: minutesAgoIso(69),
    },
    {
      id: 'wd-004', endpoint_id: 'ep-slack-flakey', subscription_id: 'sub-slack-all-runs',
      status: 'FAILED', event_type: 'io.datarecs.reconciliation.run.completed', event_id: 'evt-9a2f7b04',
      attempt_count: 4, last_response_code: 500, last_error: 'HTTP 500 Internal Server Error',
      payload: '{}', created_at: minutesAgoIso(95), updated_at: minutesAgoIso(80),
    },
    {
      id: 'wd-005', endpoint_id: 'ep-billing-dead', subscription_id: 'sub-analytics-all',
      status: 'FAILED', event_type: 'io.datarecs.reconciliation.run.completed', event_id: 'evt-c41e6d22',
      attempt_count: 8, last_response_code: null,
      last_error: 'connect ETIMEDOUT — destination host did not respond within 10s',
      payload: '{}', created_at: minutesAgoIso(130), updated_at: minutesAgoIso(60),
    },
  ];

  /** Per-delivery attempt history, keyed by delivery id — fed to the delivery detail view. */
  private readonly webhookDeliveryAttempts: Record<string, any[]> = {
    'wd-001': [
      { id: 'wa-001-1', delivery_id: 'wd-001', attempt_number: 1, response_code: 202, response_body: '{"status":"queued"}', error: null, attempted_at: minutesAgoIso(18), duration_ms: 142 },
    ],
    'wd-002': [
      { id: 'wa-002-1', delivery_id: 'wd-002', attempt_number: 1, response_code: 429, response_body: '{"error":"rate_limited","retry_after":30}', error: null, attempted_at: minutesAgoIso(42), duration_ms: 88 },
      { id: 'wa-002-2', delivery_id: 'wd-002', attempt_number: 2, response_code: 429, response_body: '{"error":"rate_limited","retry_after":60}', error: null, attempted_at: minutesAgoIso(38), duration_ms: 91 },
      { id: 'wa-002-3', delivery_id: 'wd-002', attempt_number: 3, response_code: 429, response_body: '{"error":"rate_limited","retry_after":120}', error: null, attempted_at: minutesAgoIso(35), duration_ms: 87 },
    ],
    'wd-003': [
      { id: 'wa-003-1', delivery_id: 'wd-003', attempt_number: 1, response_code: 503, response_body: 'Service Unavailable', error: null, attempted_at: minutesAgoIso(70), duration_ms: 204 },
      { id: 'wa-003-2', delivery_id: 'wd-003', attempt_number: 2, response_code: 200, response_body: 'ok', error: null, attempted_at: minutesAgoIso(69), duration_ms: 156 },
    ],
    'wd-004': [
      { id: 'wa-004-1', delivery_id: 'wd-004', attempt_number: 1, response_code: 500, response_body: '<html><body>Internal Server Error</body></html>', error: null, attempted_at: minutesAgoIso(95), duration_ms: 312 },
      { id: 'wa-004-2', delivery_id: 'wd-004', attempt_number: 2, response_code: 500, response_body: '<html><body>Internal Server Error</body></html>', error: null, attempted_at: minutesAgoIso(90), duration_ms: 298 },
      { id: 'wa-004-3', delivery_id: 'wd-004', attempt_number: 3, response_code: 502, response_body: 'Bad Gateway', error: null, attempted_at: minutesAgoIso(85), duration_ms: 401 },
      { id: 'wa-004-4', delivery_id: 'wd-004', attempt_number: 4, response_code: 500, response_body: '<html><body>Internal Server Error</body></html>', error: null, attempted_at: minutesAgoIso(80), duration_ms: 287 },
    ],
    'wd-005': [
      { id: 'wa-005-1', delivery_id: 'wd-005', attempt_number: 1, response_code: null, response_body: null, error: 'connect ETIMEDOUT 203.0.113.10:443', attempted_at: minutesAgoIso(130), duration_ms: 10002 },
      { id: 'wa-005-2', delivery_id: 'wd-005', attempt_number: 2, response_code: null, response_body: null, error: 'connect ETIMEDOUT 203.0.113.10:443', attempted_at: minutesAgoIso(110), duration_ms: 10003 },
      { id: 'wa-005-3', delivery_id: 'wd-005', attempt_number: 3, response_code: null, response_body: null, error: 'connect ETIMEDOUT 203.0.113.10:443', attempted_at: minutesAgoIso(60), duration_ms: 10001 },
    ],
  };

  private readonly webhookSecrets = new Map<string, Array<{ name: string; setAt: string }>>([
    ['ep-pagerduty', [{ name: 'routing_key', setAt: daysAgoIso(10) }]],
    ['ep-slack-flakey', [{ name: 'bot_token', setAt: daysAgoIso(21) }]]
  ]);

  private emailSettings: TenantEmailSettings = {
    email_detail_level: 'FULL_DATA' as EmailDetailLevel,
    allow_workspace_override: true,
    max_email_rows: 100,
    template_footer_text: 'Sent by DataRecs — Local Development Environment',
    template_legal_disclaimer: ''
  } as TenantEmailSettings;

  private emailRecipientRules: Array<{
    id: string;
    rule_type: EmailRecipientRuleType;
    pattern: string;
    description?: string;
  }> = [
    { id: 'demo-rule-1', rule_type: 'EXACT' as EmailRecipientRuleType, pattern: 'alerts@acme.com', description: 'Primary alerts inbox' },
    { id: 'demo-rule-2', rule_type: 'DOMAIN' as EmailRecipientRuleType, pattern: '*@acme.com', description: 'All Acme employees' },
    { id: 'demo-rule-3', rule_type: 'WILDCARD' as EmailRecipientRuleType, pattern: 'finance-*@acme.com', description: 'Finance team distribution lists' },
  ];
  private nextRuleId = 4;

  private kekData = {
    name: 'tenant-demo-tenant-0001',
    ownership: 'managed' as 'managed' | 'byok',
    residence: 'GCP KMS eu-west-2',
    last_rotated: daysAgoIso(90),
    status: 'Active'
  };

  private storageData = {
    ownership: 'managed' as 'managed' | 'byos',
    bucket_name: 'datarecs-platform-demo-tenant-0001',
    gcp_project: undefined as string | undefined,
    region: 'EU (multi-region)',
    status: 'Active',
    service_account: undefined as string | undefined,
  };

  /**
   * BYOS storage migration/revert progress — polled every 5s by `ByosMigrationService`
   * via GET /data-storage/migration-status + /migration-progress once
   * `byos-setup.component.ts` (migrate) or `data-storage-list.component.ts` (revert)
   * kicks a migration off. `polls` advances the phase each time the status endpoint is
   * hit so the migration banner + progress bar have something real to animate through
   * (preparing -> copying -> verifying -> completed) instead of jumping straight to done.
   */
  private byosMigration = { active: false, polls: 0 };

  /** Same pattern as `byosMigration`, for the BYOK KEK migration polled by `ByokMigrationService`. */
  private byokMigration = { active: false, polls: 0 };

  private auditLogEntries: AuditLogEntry[] = [];

  private roleBindings: any[] = [];
  private nextRoleBindingId = 1;

  private oidcConnectors: any[] = [
    {
      id: 'oidc-conn-1',
      name: 'Production Deploy Pipeline',
      issuer_url: 'https://token.actions.githubusercontent.com',
      audience: 'api.datarecs.io',
      organization: 'acme-corp',
      repository: 'platform-api',
      access_filter_type: 'branch',
      access_filter_value: 'main',
      subject_claim: 'repo:acme-corp/platform-api:ref:refs/heads/main',
      claim_conditions: [{ claim_name: 'ref', operator: 'equals', expected_value: 'refs/heads/main' }],
      permissions: ['list_jobs', 'view_job', 'run_job'],
      created_at: daysAgoIso(15),
      updated_at: daysAgoIso(15),
      status: 'active',
    }
  ];

  private ssoConnection: any | null = null;
  private ssoMigrationStatus: 'pending' | 'in_progress' | 'completed' | 'failed' = 'pending';

  private readonly authSessions: any[] = [
    {
      id: 'sess-demo-browser',
      user_agent: 'Chrome on macOS',
      ip_address: '203.0.113.42',
      created_at: daysAgoIso(2),
      last_seen_at: minutesAgoIso(8),
      current: true
    },
    {
      id: 'sess-demo-cli',
      user_agent: 'DataRecs CLI',
      ip_address: '203.0.113.42',
      created_at: daysAgoIso(14),
      last_seen_at: daysAgoIso(1),
      current: false
    }
  ];

  private readonly deviceSessions: any[] = [
    {
      jti: 'device-demo-terminal',
      device_name: 'Finance close terminal',
      created_at: daysAgoIso(10),
      last_used_at: daysAgoIso(1)
    }
  ];

  private readonly supportConversations: DemoSupportConversation[] = [
    {
      id: 'support-demo-1',
      subject: 'Confirm failed webhook replay behaviour',
      status: 'open',
      created_at: daysAgoIso(2),
      // Never viewed yet — the seeded agent message below is unread, so the sidebar
      // dot and the list-row badge are both lit on first load of the demo build.
      contactLastViewedAt: new Date(0).toISOString()
    }
  ];

  private readonly supportMessages = new Map<string, DemoSupportMessage[]>([
    ['support-demo-1', [
      {
        id: 'support-msg-1',
        conversation_id: 'support-demo-1',
        body: 'Can you confirm whether failed webhook deliveries are safe to replay in bulk?',
        author_type: 'contact',
        author_name: 'Niamh Demo',
        created_at: daysAgoIso(2)
      },
      {
        id: 'support-msg-2',
        conversation_id: 'support-demo-1',
        body: 'Yes. Bulk replay only re-enqueues deliveries matching the endpoint/status filters.',
        author_type: 'agent',
        author_name: 'DataRecs Support',
        created_at: minutesAgoIso(35)
      }
    ]]
  ]);

  /**
   * Messages for a conversation, oldest first, with any not-yet-"arrived" (future-created_at)
   * auto-reply filtered out — see `scheduleDemoSupportAutoReply()`. Serialized into the real
   * `SupportMessage` shape: the internal `DemoSupportMessage` stores `author_type`/`author_name`
   * as flat fields, but the shared contract (data-models-core-api) nests them as
   * `author: { type, name }` — this mapping is REQUIRED, not cosmetic: the console template
   * reads `message.author.type` directly and throws if `author` is undefined.
   */
  private visibleSupportMessages(conversationId: string): SupportMessage[] {
    const now = Date.now();
    return (this.supportMessages.get(conversationId) || [])
      .filter(m => new Date(m.created_at).getTime() <= now)
      .map(m => this.toSupportMessageDto(m));
  }

  private toSupportMessageDto(m: DemoSupportMessage): SupportMessage {
    return {
      id: m.id,
      conversation_id: m.conversation_id,
      body: m.body,
      author: { type: m.author_type as SupportMessage['author']['type'], name: m.author_name },
      attachments: [],
      created_at: m.created_at
    };
  }

  /** Compute the client-facing `SupportConversation` DTO fresh from currently-visible messages + view state — never stored, so it can't go stale. */
  private toSupportConversationDto(conversation: DemoSupportConversation): { id: string; subject: string; status: string; created_at: string; last_message_at: string | null; unread_count: number } {
    const visible = this.visibleSupportMessages(conversation.id);
    const lastMessageAt = visible.length ? visible[visible.length - 1].created_at : null;
    const lastViewedMs = new Date(conversation.contactLastViewedAt).getTime();
    const unreadCount = visible.filter(m => m.author.type === 'agent' && new Date(m.created_at).getTime() > lastViewedMs).length;
    return {
      id: conversation.id,
      subject: conversation.subject,
      status: conversation.status,
      created_at: conversation.created_at,
      last_message_at: lastMessageAt,
      unread_count: unreadCount
    };
  }

  /**
   * Schedule a canned agent auto-reply `DEMO_SUPPORT_AGENT_REPLY_DELAY_MS` after a customer
   * message — invisible until that much (real or Playwright-virtual-clock) time has passed,
   * per `visibleSupportMessages()`. This is what lets the reply-loop poll (`SupportDetailComponent`,
   * every 30s) and the sidebar unread badge (`SupportUnreadService`, every 60s) demonstrate a
   * real round trip in the demo build, which has no simulated support agent otherwise.
   */
  private scheduleDemoSupportAutoReply(conversationId: string, afterIso: string): void {
    const repliedAt = new Date(new Date(afterIso).getTime() + DEMO_SUPPORT_AGENT_REPLY_DELAY_MS).toISOString();
    const reply: DemoSupportMessage = {
      id: this.generateId('support-msg'),
      conversation_id: conversationId,
      body: "Thanks for the update — we're on it and will follow up here shortly.",
      author_type: 'agent',
      author_name: 'DataRecs Support',
      created_at: repliedAt
    };
    const messages = this.supportMessages.get(conversationId) || [];
    this.supportMessages.set(conversationId, [...messages, reply]);
  }

  constructor() {
    this.seedJobs();
    this.seedAuditLog();
    this.migrateRoleAssignments();
  }


  private seedJobs(): void {
    if (this.jobs.length) {
      return;
    }

    const seeds: JobSeed[] = [
      // ── L1: Simple 2-source comparisons ──────────────────────────────
      {
        id: 'job-l1-customers-pg-db2',
        name: 'Customers (Postgres ↔ DB2)',
        description: 'Simple row-count comparison of customer records between Postgres and DB2.',
        workspace_id: 'ws-ecommerce',
        job_group_id: 'jg-ecommerce-integrity',
        schema: 'CUSTOMERS',
        table: 'customers',
        connections: ['conn-postgres-ecommerce', 'conn-db2-ecommerce'],
        dimensions: ['customer_id'],
        measures: [
          { alias: 'customer_count', column: 'customer_id', aggregation: 'COUNT' as AggregationType }
        ],
        stages: [
          {
            dimensions: ['customer_id'],
            tolerances: [
              { measure: 'customer_count', type: 'ABSOLUTE' as ToleranceType, value: 0 }
            ]
          }
        ]
      },
      {
        id: 'job-l1-customers-pg-oracle',
        name: 'Customers (Postgres ↔ Oracle)',
        description: 'Simple row-count comparison of customer records between Postgres and Oracle.',
        workspace_id: 'ws-ecommerce',
        job_group_id: 'jg-ecommerce-integrity',
        schema: 'CUSTOMERS',
        table: 'customers',
        connections: ['conn-postgres-ecommerce', 'conn-oracle-ecommerce'],
        dimensions: ['customer_id'],
        measures: [
          { alias: 'customer_count', column: 'customer_id', aggregation: 'COUNT' as AggregationType }
        ],
        stages: [
          {
            dimensions: ['customer_id'],
            tolerances: [
              { measure: 'customer_count', type: 'ABSOLUTE' as ToleranceType, value: 0 }
            ]
          }
        ]
      },
      {
        id: 'job-l1-orders-pg-mssql',
        name: 'Orders (Postgres ↔ SQL Server)',
        description: 'Validates order totals between Postgres and SQL Server replicas.',
        workspace_id: 'ws-ecommerce',
        job_group_id: 'jg-ecommerce-integrity',
        schema: 'SALES',
        table: 'orders',
        connections: ['conn-postgres-ecommerce', 'conn-mssql-ecommerce'],
        dimensions: ['order_id'],
        measures: [
          { alias: 'total_amount', column: 'total_amount', aggregation: 'SUM' as AggregationType }
        ],
        stages: [
          {
            dimensions: ['order_id'],
            tolerances: [
              { measure: 'total_amount', type: 'ABSOLUTE' as ToleranceType, value: 1 }
            ]
          }
        ]
      },

      // ── L2: Filtered query comparisons ───────────────────────────────
      {
        id: 'job-l2-orders-status-filter',
        name: 'Orders by Status (Filtered)',
        description: 'Compares completed orders only using WHERE status = completed.',
        workspace_id: 'ws-ecommerce',
        job_group_id: 'jg-ecommerce-integrity',
        schema: 'SALES',
        table: 'orders',
        connections: ['conn-postgres-ecommerce', 'conn-oracle-ecommerce'],
        dimensions: ['order_id', 'status'],
        measures: [
          { alias: 'total_amount', column: 'total_amount', aggregation: 'SUM' as AggregationType }
        ],
        stages: [
          {
            dimensions: ['order_id'],
            tolerances: [
              { measure: 'total_amount', type: 'ABSOLUTE' as ToleranceType, value: 2 }
            ]
          }
        ]
      },
      {
        id: 'job-l2-campaigns-active-filter',
        name: 'Active Campaigns (Filtered)',
        description: 'Compares only active LinkedIn campaigns using WHERE campaign_status = ACTIVE.',
        workspace_id: 'ws-marketing',
        job_group_id: 'jg-marketing-spend',
        schema: 'ADVERTISING',
        table: 'linkedin_campaign_performance_report',
        connections: ['conn-postgres-marketing', 'conn-mysql-marketing'],
        dimensions: ['campaign_id', 'campaign_status'],
        measures: [
          { alias: 'total_spent', column: 'total_spent', aggregation: 'SUM' as AggregationType },
          { alias: 'impressions', column: 'impressions', aggregation: 'SUM' as AggregationType }
        ],
        stages: [
          {
            dimensions: ['campaign_id'],
            tolerances: [
              { measure: 'total_spent', type: 'PERCENT' as ToleranceType, value: 1 },
              { measure: 'impressions', type: 'PERCENT' as ToleranceType, value: 2 }
            ]
          }
        ]
      },
      {
        id: 'job-l2-page-stats-date-filter',
        name: 'Page Stats by Date (Filtered)',
        description: 'Compares page stats for a specific date range using WHERE date filter.',
        workspace_id: 'ws-analytics',
        job_group_id: 'jg-analytics-pipeline',
        schema: 'WEB_ANALYTICS',
        table: 'daily_page_stats',
        connections: ['conn-postgres-analytics', 'conn-mysql-analytics'],
        dimensions: ['date', 'page_url'],
        measures: [
          { alias: 'total_views', column: 'total_views', aggregation: 'SUM' as AggregationType }
        ],
        stages: [
          {
            dimensions: ['date'],
            tolerances: [
              { measure: 'total_views', type: 'PERCENT' as ToleranceType, value: 3 }
            ]
          }
        ]
      },

      // ── L3: Numeric aggregation types ────────────────────────────────
      {
        id: 'job-l3-product-price-aggs',
        name: 'Product Price Aggregations',
        description: 'Validates SUM, MIN, MAX, AVG of product prices across replicas.',
        workspace_id: 'ws-ecommerce',
        job_group_id: 'jg-ecommerce-integrity',
        schema: 'PRODUCTS',
        table: 'products',
        connections: ['conn-postgres-ecommerce', 'conn-mssql-ecommerce'],
        dimensions: ['category'],
        measures: [
          { alias: 'sum_price', column: 'price', aggregation: 'SUM' as AggregationType },
          { alias: 'min_price', column: 'price', aggregation: 'MIN' as AggregationType },
          { alias: 'max_price', column: 'price', aggregation: 'MAX' as AggregationType },
          { alias: 'avg_price', column: 'price', aggregation: 'AVG' as AggregationType }
        ],
        stages: [
          {
            dimensions: ['category'],
            tolerances: [
              { measure: 'sum_price', type: 'ABSOLUTE' as ToleranceType, value: 5 },
              { measure: 'min_price', type: 'ABSOLUTE' as ToleranceType, value: 1 },
              { measure: 'max_price', type: 'ABSOLUTE' as ToleranceType, value: 1 },
              { measure: 'avg_price', type: 'PERCENT' as ToleranceType, value: 2 }
            ]
          }
        ]
      },
      {
        id: 'job-l3-campaign-count-distinct',
        name: 'Campaign Count Distinct',
        description: 'Validates COUNT and COUNT_DISTINCT of campaigns across sources.',
        workspace_id: 'ws-marketing',
        job_group_id: 'jg-marketing-spend',
        schema: 'ADVERTISING',
        table: 'linkedin_campaign_performance_report',
        connections: ['conn-postgres-marketing', 'conn-db2-marketing'],
        dimensions: ['currency'],
        measures: [
          { alias: 'campaign_count', column: 'campaign_id', aggregation: 'COUNT' as AggregationType },
          { alias: 'distinct_campaigns', column: 'campaign_id', aggregation: 'COUNT_DISTINCT' as AggregationType }
        ],
        stages: [
          {
            dimensions: ['currency'],
            tolerances: [
              { measure: 'campaign_count', type: 'ABSOLUTE' as ToleranceType, value: 0 },
              { measure: 'distinct_campaigns', type: 'ABSOLUTE' as ToleranceType, value: 0 }
            ]
          }
        ]
      },

      // ── L4: Multi-stage sequential execution ─────────────────────────
      {
        id: 'job-l4-orders-two-stage',
        name: 'Orders Two-Stage Validation',
        description: 'Stage 1: match by order_id. Stage 2: aggregate by status.',
        workspace_id: 'ws-ecommerce',
        job_group_id: 'jg-ecommerce-integrity',
        schema: 'SALES',
        table: 'orders',
        connections: ['conn-oracle-ecommerce', 'conn-mssql-ecommerce'],
        dimensions: ['order_id', 'status'],
        measures: [
          { alias: 'order_count', column: 'order_id', aggregation: 'COUNT' as AggregationType },
          { alias: 'total_amount', column: 'total_amount', aggregation: 'SUM' as AggregationType }
        ],
        stages: [
          {
            dimensions: ['order_id'],
            tolerances: [
              { measure: 'total_amount', type: 'ABSOLUTE' as ToleranceType, value: 2 }
            ]
          },
          {
            dimensions: ['status'],
            tolerances: [
              { measure: 'total_amount', type: 'PERCENT' as ToleranceType, value: 0.5 },
              { measure: 'order_count', type: 'ABSOLUTE' as ToleranceType, value: 5 }
            ]
          }
        ]
      },
      {
        id: 'job-l4-spend-two-stage',
        name: 'LinkedIn Spend Two-Stage',
        description: 'Stage 1: match by campaign_id. Stage 2: aggregate by campaign_name.',
        workspace_id: 'ws-marketing',
        job_group_id: 'jg-marketing-spend',
        schema: 'ADVERTISING',
        table: 'linkedin_campaign_performance_report',
        connections: ['conn-postgres-marketing', 'conn-mysql-marketing'],
        dimensions: ['campaign_id', 'campaign_name', 'currency'],
        measures: [
          { alias: 'total_spent', column: 'total_spent', aggregation: 'SUM' as AggregationType },
          { alias: 'clicks', column: 'clicks', aggregation: 'SUM' as AggregationType }
        ],
        stages: [
          {
            dimensions: ['campaign_id', 'currency'],
            tolerances: [
              { measure: 'total_spent', type: 'PERCENT' as ToleranceType, value: 1 },
              { measure: 'clicks', type: 'PERCENT' as ToleranceType, value: 2 }
            ]
          },
          {
            dimensions: ['campaign_name'],
            tolerances: [
              { measure: 'total_spent', type: 'ABSOLUTE' as ToleranceType, value: 500 }
            ]
          }
        ]
      },

      // ── L5: Multi-stage drill-down-on-failure ────────────────────────
      {
        id: 'job-l5-page-stats-drilldown',
        name: 'Page Stats Drill-Down',
        description: 'Stage 1: aggregate by date. On failure, Stage 2 drills down to page_url.',
        workspace_id: 'ws-analytics',
        job_group_id: 'jg-analytics-pipeline',
        schema: 'WEB_ANALYTICS',
        table: 'daily_page_stats',
        connections: ['conn-db2-analytics', 'conn-postgres-analytics'],
        dimensions: ['date', 'page_url'],
        measures: [
          { alias: 'total_views', column: 'total_views', aggregation: 'SUM' as AggregationType },
          { alias: 'unique_users', column: 'unique_users', aggregation: 'SUM' as AggregationType }
        ],
        stages: [
          {
            dimensions: ['date'],
            tolerances: [
              { measure: 'total_views', type: 'PERCENT' as ToleranceType, value: 2 },
              { measure: 'unique_users', type: 'PERCENT' as ToleranceType, value: 2 }
            ]
          },
          {
            dimensions: ['date', 'page_url'],
            tolerances: [
              { measure: 'total_views', type: 'ABSOLUTE' as ToleranceType, value: 10 },
              { measure: 'unique_users', type: 'ABSOLUTE' as ToleranceType, value: 5 }
            ]
          }
        ]
      },
      {
        id: 'job-l5-shipments-drilldown',
        name: 'Shipments Drill-Down',
        description: 'Stage 1: aggregate by status. On failure, Stage 2 drills down to order_id.',
        workspace_id: 'ws-ecommerce',
        job_group_id: 'jg-ecommerce-integrity',
        schema: 'SALES',
        table: 'shipments',
        connections: ['conn-postgres-ecommerce', 'conn-db2-ecommerce'],
        dimensions: ['order_id', 'status'],
        measures: [
          { alias: 'shipment_count', column: 'shipment_id', aggregation: 'COUNT' as AggregationType }
        ],
        stages: [
          {
            dimensions: ['status'],
            tolerances: [
              { measure: 'shipment_count', type: 'ABSOLUTE' as ToleranceType, value: 5 }
            ]
          },
          {
            dimensions: ['order_id'],
            tolerances: [
              { measure: 'shipment_count', type: 'ABSOLUTE' as ToleranceType, value: 0 }
            ]
          }
        ]
      },

      // ── L6: Mixed ABSOLUTE and PERCENT tolerances ────────────────────
      {
        id: 'job-l6-sales-overview-mixed',
        name: 'Sales Overview Mixed Tolerances',
        description: 'Uses ABSOLUTE tolerance for total_sales and PERCENT for total_profit.',
        workspace_id: 'ws-analytics',
        job_group_id: 'jg-analytics-pipeline',
        schema: 'DASHBOARDS',
        table: 'sales_overview',
        connections: ['conn-postgres-analytics', 'conn-mssql-analytics'],
        dimensions: ['date'],
        measures: [
          { alias: 'total_sales', column: 'total_sales', aggregation: 'SUM' as AggregationType },
          { alias: 'total_profit', column: 'total_profit', aggregation: 'SUM' as AggregationType },
          { alias: 'unique_customers', column: 'unique_customers', aggregation: 'SUM' as AggregationType }
        ],
        stages: [
          {
            dimensions: ['date'],
            tolerances: [
              { measure: 'total_sales', type: 'ABSOLUTE' as ToleranceType, value: 100 },
              { measure: 'total_profit', type: 'PERCENT' as ToleranceType, value: 3 },
              { measure: 'unique_customers', type: 'ABSOLUTE' as ToleranceType, value: 2 }
            ]
          }
        ]
      },
      {
        id: 'job-l6-campaign-mixed-tol',
        name: 'Campaign Spend Mixed Tolerances',
        description: 'ABSOLUTE tolerance on clicks, PERCENT on total_spent.',
        workspace_id: 'ws-marketing',
        job_group_id: 'jg-marketing-spend',
        schema: 'ADVERTISING',
        table: 'linkedin_campaign_performance_report',
        connections: ['conn-mysql-marketing', 'conn-oracle-marketing'],
        dimensions: ['campaign_id'],
        measures: [
          { alias: 'total_spent', column: 'total_spent', aggregation: 'SUM' as AggregationType },
          { alias: 'clicks', column: 'clicks', aggregation: 'SUM' as AggregationType }
        ],
        stages: [
          {
            dimensions: ['campaign_id'],
            tolerances: [
              { measure: 'total_spent', type: 'PERCENT' as ToleranceType, value: 1.5 },
              { measure: 'clicks', type: 'ABSOLUTE' as ToleranceType, value: 10 }
            ]
          }
        ]
      },

      // ── L7: Multi-source comparisons (3-way, 4-way, 5-way) ──────────
      {
        id: 'job-l7-customers-3way',
        name: 'Customers 3-Way (PG ↔ MySQL ↔ Oracle)',
        description: '3-way comparison of customer records across Postgres, MySQL, and Oracle.',
        workspace_id: 'ws-ecommerce',
        job_group_id: 'jg-ecommerce-integrity',
        schema: 'CUSTOMERS',
        table: 'customers',
        connections: ['conn-postgres-ecommerce', 'conn-mysql-ecommerce', 'conn-oracle-ecommerce'],
        dimensions: ['customer_id'],
        measures: [
          { alias: 'customer_count', column: 'customer_id', aggregation: 'COUNT' as AggregationType }
        ],
        stages: [
          {
            dimensions: ['customer_id'],
            tolerances: [
              { measure: 'customer_count', type: 'ABSOLUTE' as ToleranceType, value: 0 }
            ]
          }
        ]
      },
      {
        id: 'job-l7-orders-4way',
        name: 'Orders 4-Way (PG ↔ MySQL ↔ MSSQL ↔ DB2)',
        description: '4-way comparison of order totals across four database engines.',
        workspace_id: 'ws-ecommerce',
        job_group_id: 'jg-ecommerce-integrity',
        schema: 'SALES',
        table: 'orders',
        connections: ['conn-postgres-ecommerce', 'conn-mysql-ecommerce', 'conn-mssql-ecommerce', 'conn-db2-ecommerce'],
        dimensions: ['order_id'],
        measures: [
          { alias: 'total_amount', column: 'total_amount', aggregation: 'SUM' as AggregationType }
        ],
        stages: [
          {
            dimensions: ['order_id'],
            tolerances: [
              { measure: 'total_amount', type: 'ABSOLUTE' as ToleranceType, value: 2 }
            ]
          }
        ]
      },
      {
        id: 'job-l7-page-stats-5way',
        name: 'Page Stats 5-Way (All Engines)',
        description: '5-way comparison of daily page stats across all five database engines.',
        workspace_id: 'ws-analytics',
        job_group_id: 'jg-analytics-pipeline',
        schema: 'WEB_ANALYTICS',
        table: 'daily_page_stats',
        connections: ['conn-postgres-analytics', 'conn-mysql-analytics', 'conn-mssql-analytics', 'conn-db2-analytics', 'conn-oracle-analytics'],
        dimensions: ['date', 'page_url'],
        measures: [
          { alias: 'total_views', column: 'total_views', aggregation: 'SUM' as AggregationType },
          { alias: 'unique_users', column: 'unique_users', aggregation: 'SUM' as AggregationType }
        ],
        stages: [
          {
            dimensions: ['date'],
            tolerances: [
              { measure: 'total_views', type: 'PERCENT' as ToleranceType, value: 3 },
              { measure: 'unique_users', type: 'PERCENT' as ToleranceType, value: 3 }
            ]
          }
        ]
      },

      // ── L8: Combined filters + multi-stage + multi-source + mixed tolerances
      {
        id: 'job-l8-spend-full-validation',
        name: 'Campaign Spend Full Validation',
        description: 'Filtered active campaigns, 3-way comparison, 2 stages, mixed tolerances.',
        workspace_id: 'ws-marketing',
        job_group_id: 'jg-marketing-spend',
        schema: 'ADVERTISING',
        table: 'linkedin_campaign_performance_report',
        connections: ['conn-postgres-marketing', 'conn-mysql-marketing', 'conn-oracle-marketing'],
        dimensions: ['campaign_id', 'campaign_name', 'currency', 'campaign_status'],
        measures: [
          { alias: 'total_spent', column: 'total_spent', aggregation: 'SUM' as AggregationType },
          { alias: 'impressions', column: 'impressions', aggregation: 'SUM' as AggregationType },
          { alias: 'clicks', column: 'clicks', aggregation: 'SUM' as AggregationType },
          { alias: 'distinct_campaigns', column: 'campaign_id', aggregation: 'COUNT_DISTINCT' as AggregationType }
        ],
        stages: [
          {
            dimensions: ['campaign_id', 'currency'],
            tolerances: [
              { measure: 'total_spent', type: 'PERCENT' as ToleranceType, value: 1 },
              { measure: 'impressions', type: 'PERCENT' as ToleranceType, value: 2 },
              { measure: 'clicks', type: 'ABSOLUTE' as ToleranceType, value: 15 }
            ]
          },
          {
            dimensions: ['campaign_name'],
            tolerances: [
              { measure: 'total_spent', type: 'ABSOLUTE' as ToleranceType, value: 500 },
              { measure: 'distinct_campaigns', type: 'ABSOLUTE' as ToleranceType, value: 0 }
            ]
          }
        ]
      },
      {
        id: 'job-l8-ecommerce-full-recon',
        name: 'E-Commerce Full Reconciliation',
        description: 'Filtered completed orders, 4-way comparison, 3 stages, mixed tolerances.',
        workspace_id: 'ws-ecommerce',
        job_group_id: 'jg-ecommerce-integrity',
        schema: 'SALES',
        table: 'orders',
        connections: ['conn-postgres-ecommerce', 'conn-mysql-ecommerce', 'conn-mssql-ecommerce', 'conn-oracle-ecommerce'],
        dimensions: ['order_id', 'customer_id', 'status'],
        measures: [
          { alias: 'total_amount', column: 'total_amount', aggregation: 'SUM' as AggregationType },
          { alias: 'order_count', column: 'order_id', aggregation: 'COUNT' as AggregationType }
        ],
        stages: [
          {
            dimensions: ['order_id'],
            tolerances: [
              { measure: 'total_amount', type: 'ABSOLUTE' as ToleranceType, value: 1 }
            ]
          },
          {
            dimensions: ['customer_id'],
            tolerances: [
              { measure: 'total_amount', type: 'PERCENT' as ToleranceType, value: 0.5 },
              { measure: 'order_count', type: 'ABSOLUTE' as ToleranceType, value: 2 }
            ]
          },
          {
            dimensions: ['status'],
            tolerances: [
              { measure: 'total_amount', type: 'PERCENT' as ToleranceType, value: 1 },
              { measure: 'order_count', type: 'PERCENT' as ToleranceType, value: 3 }
            ]
          }
        ]
      },
      {
        id: 'job-l8-analytics-full-pipeline',
        name: 'Analytics Full Pipeline Check',
        description: 'Filtered by date, 3-way comparison, 2 stages, mixed tolerances.',
        workspace_id: 'ws-analytics',
        job_group_id: 'jg-analytics-pipeline',
        schema: 'WEB_ANALYTICS',
        table: 'daily_page_stats',
        connections: ['conn-postgres-analytics', 'conn-mysql-analytics', 'conn-db2-analytics'],
        dimensions: ['date', 'page_url'],
        measures: [
          { alias: 'total_views', column: 'total_views', aggregation: 'SUM' as AggregationType },
          { alias: 'unique_users', column: 'unique_users', aggregation: 'SUM' as AggregationType },
          { alias: 'avg_time', column: 'avg_time_on_page', aggregation: 'AVG' as AggregationType }
        ],
        stages: [
          {
            dimensions: ['date'],
            tolerances: [
              { measure: 'total_views', type: 'PERCENT' as ToleranceType, value: 2 },
              { measure: 'unique_users', type: 'ABSOLUTE' as ToleranceType, value: 20 },
              { measure: 'avg_time', type: 'PERCENT' as ToleranceType, value: 5 }
            ]
          },
          {
            dimensions: ['date', 'page_url'],
            tolerances: [
              { measure: 'total_views', type: 'ABSOLUTE' as ToleranceType, value: 10 },
              { measure: 'unique_users', type: 'ABSOLUTE' as ToleranceType, value: 5 }
            ]
          }
        ]
      }
    ];

    // Realistic row counts per job based on complexity
    const rowCountProfiles = [
      { processed: 8500, matched: 8490, unmatched: 10 },     // L1
      { processed: 9200, matched: 9185, unmatched: 15 },     // L1
      { processed: 12400, matched: 12350, unmatched: 50 },   // L1
      { processed: 6800, matched: 6720, unmatched: 80 },     // L2
      { processed: 4500, matched: 4460, unmatched: 40 },     // L2
      { processed: 7200, matched: 7150, unmatched: 50 },     // L2
      { processed: 3200, matched: 3180, unmatched: 20 },     // L3
      { processed: 2800, matched: 2795, unmatched: 5 },      // L3
      { processed: 18500, matched: 18200, unmatched: 300 },  // L4
      { processed: 15000, matched: 14800, unmatched: 200 },  // L4
      { processed: 22000, matched: 21500, unmatched: 500 },  // L5
      { processed: 11000, matched: 10850, unmatched: 150 },  // L5
      { processed: 7600, matched: 7520, unmatched: 80 },     // L6
      { processed: 5400, matched: 5350, unmatched: 50 },     // L6
      { processed: 9800, matched: 9750, unmatched: 50 },     // L7
      { processed: 25000, matched: 24600, unmatched: 400 },  // L7
      { processed: 31000, matched: 30200, unmatched: 800 },  // L7
      { processed: 14200, matched: 13900, unmatched: 300 },  // L8
      { processed: 28000, matched: 27200, unmatched: 800 },  // L8
      { processed: 19500, matched: 19000, unmatched: 500 },  // L8
    ];

    seeds.forEach((seed, index) => {
      const job = this.createJobFromSeed(seed, index);
      (job as any).parent_job_group_id = seed.job_group_id;
      this.jobs.push(job);
      this.attachJobToGroup(seed.job_group_id, job);

      const profile = rowCountProfiles[index % rowCountProfiles.length];
      const completedRun = this.createJobRun(job, {
        status: 'COMPLETED',
        run_label: 'Daily window',
        started_at: daysAgoIso(0.04 + index * 0.01),
        completed_at: daysAgoIso(0.035 + index * 0.008),
        rows_processed: profile.processed,
        rows_matched: profile.matched,
        rows_unmatched: profile.unmatched
      });
      job.last_run_at = completedRun.completed_at;
      job.last_run_status = 'COMPLETED' as JobRunStatus;
      job.last_successful_run_at = completedRun.completed_at;
      if (completedRun.completed_at) {
        job.updated_at = new Date(completedRun.completed_at);
        (job as any).updatedAt = completedRun.completed_at;
      }
      this.recordRun(job.id, completedRun);

      // Seed additional historical runs (some failed) for richer stats
      if (index % 3 === 0) {
        // Add a failed run from ~2 days ago
        const failedRun = this.createJobRun(job, {
          status: 'ERRORED',
          run_label: 'Daily window',
          started_at: daysAgoIso(2 + index * 0.1),
          completed_at: daysAgoIso(1.99 + index * 0.1),
          rows_processed: 0,
          rows_matched: 0,
          rows_unmatched: 0,
          error: { code: 'CONNECTION_TIMEOUT', message: 'Connection timed out after 30s' }
        });
        this.recordRun(job.id, failedRun);
      }
      if (index % 5 === 1) {
        // Add another failed run from ~5 days ago
        const failedRun2 = this.createJobRun(job, {
          status: 'ERRORED',
          run_label: 'Scheduled run',
          started_at: daysAgoIso(5 + index * 0.05),
          completed_at: daysAgoIso(4.99 + index * 0.05),
          rows_processed: 0,
          rows_matched: 0,
          rows_unmatched: 0,
          error: { code: 'AUTH_ERROR', message: 'Authentication failed for data source' }
        });
        this.recordRun(job.id, failedRun2);
      }
      // Add a completed run from ~3 days ago for all jobs
      const olderRun = this.createJobRun(job, {
        status: 'COMPLETED',
        run_label: 'Daily window',
        started_at: daysAgoIso(3 + index * 0.05),
        completed_at: daysAgoIso(2.98 + index * 0.05),
        rows_processed: Math.round(profile.processed * 0.95),
        rows_matched: Math.round(profile.matched * 0.95),
        rows_unmatched: Math.round(profile.unmatched * 1.1)
      });
      this.recordRun(job.id, olderRun);

      // Seed one persistent run in each of the remaining statuses (RUNNING, QUEUED, CANCELLED)
      // that `simulateRunLifecycle`/manual-trigger only ever produce transiently — the job-runs
      // list, dashboard, and recent-runs views need at least one durable example of every
      // JobRunStatus so their status badges are exercisable without triggering a run first.
      if (index % 7 === 2) {
        const runningRun = this.createJobRun(job, {
          status: 'RUNNING',
          run_label: 'Daily window',
          // Must stay older than the COMPLETED "Daily window" run above
          // (daysAgoIso(3 + index * 0.05)) — the results list sorts by
          // started_at descending, and several tests (e.g. journeys.spec.ts
          // J30) assert the most recent run in the table is COMPLETED.
          started_at: daysAgoIso(3.5 + index * 0.05),
          rows_processed: Math.round(profile.processed * 0.4),
          rows_matched: Math.round(profile.matched * 0.4),
          rows_unmatched: Math.round(profile.unmatched * 0.4)
        });
        this.recordRun(job.id, runningRun);
      }
      if (index % 7 === 4) {
        const queuedRun = this.createJobRun(job, {
          status: 'QUEUED',
          run_label: 'Daily window'
        });
        this.recordRun(job.id, queuedRun);
      }
      if (index % 7 === 6) {
        const cancelledRun = this.createJobRun(job, {
          status: 'CANCELLED',
          run_label: 'Daily window',
          started_at: daysAgoIso(1 + index * 0.02),
          completed_at: daysAgoIso(0.99 + index * 0.02),
          rows_processed: Math.round(profile.processed * 0.2),
          rows_matched: Math.round(profile.matched * 0.2),
          rows_unmatched: 0
        });
        this.recordRun(job.id, cancelledRun);
      }

      this.jobSchedules.set(job.id, [
        {
          schedule_id: this.generateId('sched'),
          job_id: job.id,
          cron_expression: index % 3 === 0 ? '0 2 * * *' : index % 3 === 1 ? '0 */6 * * *' : '0 8 * * 1-5',
          timezone: 'UTC',
          enabled: true,
          params: { window: 'previous_day' },
          next_run_at: new Date(Date.now() + (index + 1) * 2 * 60 * 60 * 1000).toISOString(),
          last_run_at: completedRun.completed_at,
          created_at: new Date().toISOString()
        }
      ]);
    });
  }

  private seedAuditLog(): void {
    if (this.auditLogEntries.length) {
      return;
    }

    const userIds = this.users.map(u => u.id);
    const userEmails = this.users.map(u => u.email);
    const workspaceIds = this.workspaces.map(w => w.id);
    const connectionIds = this.connections.map(c => c.summary.id);
    const jobIds = this.jobs.map(j => j.id);
    const webhookEndpointIds = this.webhookEndpoints.map(e => e.id);

    const pick = <T>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

    const entryDefs: Array<{
      type: string;
      source: string;
      buildSubject: () => string;
      buildData: () => Record<string, unknown>;
    }> = [
      {
        type: 'io.datarecs.identity.user.authenticated',
        source: 'io.datarecs.identity',
        buildSubject: () => pick(userIds),
        buildData: () => ({ user_email: pick(userEmails), ip_address: '10.0.1.42', user_agent: 'Mozilla/5.0' })
      },
      {
        type: 'io.datarecs.workspace.workspace.created',
        source: 'io.datarecs.workspace',
        buildSubject: () => pick(workspaceIds),
        buildData: () => ({ workspace_name: pick(this.workspaces).name, created_by: pick(userEmails) })
      },
      {
        type: 'io.datarecs.connection.connection.created',
        source: 'io.datarecs.connection',
        buildSubject: () => pick(connectionIds),
        buildData: () => {
          const conn = pick(this.connections);
          return { connection_name: conn.summary.name, connection_type: conn.summary.type, workspace_id: conn.summary.workspace_id };
        }
      },
      {
        type: 'io.datarecs.connection.connection.tested',
        source: 'io.datarecs.connection',
        buildSubject: () => pick(connectionIds),
        buildData: () => {
          const conn = pick(this.connections);
          return { connection_name: conn.summary.name, result: 'success', latency_ms: Math.floor(Math.random() * 200) + 50 };
        }
      },
      {
        type: 'io.datarecs.reconciliation.job.created',
        source: 'io.datarecs.reconciliation',
        buildSubject: () => pick(jobIds),
        buildData: () => {
          const job = pick(this.jobs);
          return { job_name: job.name, workspace_id: job.workspace_id, created_by: pick(userEmails) };
        }
      },
      {
        type: 'io.datarecs.reconciliation.job.triggered',
        source: 'io.datarecs.reconciliation',
        buildSubject: () => pick(jobIds),
        buildData: () => {
          const job = pick(this.jobs);
          return { job_name: job.name, triggered_by: pick(userEmails), mode: 'FULL' };
        }
      },
      {
        type: 'io.datarecs.reconciliation.run.started',
        source: 'io.datarecs.reconciliation',
        buildSubject: () => pick(jobIds),
        buildData: () => ({ run_id: this.generateId('run'), job_name: pick(this.jobs).name })
      },
      {
        type: 'io.datarecs.reconciliation.run.completed',
        source: 'io.datarecs.reconciliation',
        buildSubject: () => pick(jobIds),
        buildData: () => ({
          run_id: this.generateId('run'),
          job_name: pick(this.jobs).name,
          rows_processed: Math.floor(Math.random() * 20000) + 5000,
          rows_matched: Math.floor(Math.random() * 18000) + 4500,
          rows_unmatched: Math.floor(Math.random() * 500) + 10,
          duration_ms: Math.floor(Math.random() * 30000) + 2000
        })
      },
      {
        type: 'io.datarecs.reconciliation.run.failed',
        source: 'io.datarecs.reconciliation',
        buildSubject: () => pick(jobIds),
        buildData: () => ({
          run_id: this.generateId('run'),
          job_name: pick(this.jobs).name,
          error_code: 'CONNECTION_TIMEOUT',
          error_message: 'Connection timed out after 30s'
        })
      },
      {
        type: 'io.datarecs.webhook.endpoint.created',
        source: 'io.datarecs.webhook',
        buildSubject: () => pick(webhookEndpointIds),
        buildData: () => {
          const ep = pick(this.webhookEndpoints);
          return { endpoint_url: ep.url, description: ep.description, created_by: pick(userEmails) };
        }
      },
      {
        type: 'io.datarecs.platform.api_key.created',
        source: 'io.datarecs.platform',
        buildSubject: () => pick(this.apiKeys).id,
        buildData: () => {
          const key = pick(this.apiKeys);
          return { key_name: key.name, prefix: key.prefix, created_by: pick(userEmails) };
        }
      }
    ];

    const totalEntries = 35;
    for (let i = 0; i < totalEntries; i++) {
      const def = entryDefs[i % entryDefs.length];
      const daysAgo = (i / totalEntries) * 30;
      const timeMs = Date.now() - daysAgo * DAY_IN_MS - Math.random() * DAY_IN_MS * 0.5;
      const time = new Date(timeMs).toISOString();
      const ingestedAt = new Date(timeMs + Math.floor(Math.random() * 5000) + 500).toISOString();

      this.auditLogEntries.push({
        id: this.generateId('audit'),
        type: def.type,
        source: def.source,
        subject: def.buildSubject(),
        time,
        data: def.buildData(),
        specversion: '1.0',
        ingested_at: ingestedAt
      });
    }

    this.auditLogEntries.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());
  }

  private migrateRoleAssignments(): void {
    this.roleAssignments.forEach((assignments, roleKey) => {
      const platformRole = this.platformRoles.find(r => r.name === roleKey);
      const customRole = this.customRoles.find(r => r.id.toString() === roleKey || r.name === roleKey);

      const roleType: string = platformRole ? 'platform' : customRole ? 'custom' : 'platform';
      const roleId = platformRole ? platformRole.id : customRole ? customRole.id : 0;
      const scope = platformRole?.scope || customRole?.scope || 'tenant';

      for (const assignment of assignments) {
        const binding = {
          // #63 model drift: RoleBinding ids are now UUID strings; demo keeps a
          // numeric counter but stores its ids as strings to satisfy the contract.
          id: String(this.nextRoleBindingId++),
          subject_type: (assignment.subject_type === 'group' ? 'group' : 'user') as RoleBindingSubjectTypes,
          subject_id: assignment.subject,
          role_type: roleType as any,
          role_id: String(roleId),
          scope: scope as any,
        };
        this.roleBindings.push(binding);
      }
    });
  }

  private createJobFromSeed(seed: JobSeed, index: number): Job {
    const payload: CreateJobPayload = {
      name: seed.name,
      workspace_id: seed.workspace_id,
      dimensions: [...seed.dimensions],
      measures: seed.measures.map(measure => ({ name: measure.alias, aggregation: measure.aggregation })),
      connections: seed.connections.map(connectionId => ({
        connection_id: connectionId,
        source_schema: seed.schema,
        source_table: seed.table,
        column_mapping: { ...this.buildSeedColumnMapping(seed) }
      })),
      stages: seed.stages.map(stage => ({
        dimensions: [...stage.dimensions],
        tolerances: stage.tolerances.map(tolerance => ({
          measure_name: tolerance.measure,
          type: tolerance.type,
          value: tolerance.value
        }))
      }))
    };
    const createdAt = daysAgoDate(25 - index * 3);
    const updatedAt = daysAgoDate(Math.max(1, 6 - index * 2));
    return this.createJobFromPayload(payload, seed.description, seed.id, { createdAt, updatedAt });
  }

  private buildSeedColumnMapping(seed: JobSeed): Record<string, string> {
    const mapping: Record<string, string> = {};
    seed.dimensions.forEach(dimension => {
      mapping[dimension] = dimension;
    });
    seed.measures.forEach(measure => {
      mapping[measure.alias] = measure.column;
    });
    return mapping;
  }

  private createJobFromPayload(payload: CreateJobPayload, description?: string, explicitId?: string, timestamps?: { createdAt?: Date; updatedAt?: Date }): Job {
    const createdAt = timestamps?.createdAt || new Date();
    const updatedAt = timestamps?.updatedAt || createdAt;
    const job: Job = {
      id: explicitId || (this.generateId('job') as any),
      name: payload.name,
      description,
      workspace_id: payload.workspace_id || 'ws-ecommerce',
      dimensions: [...(payload.dimensions || [])],
      measures: (payload.measures || []).map(measure => ({
        name: measure.name,
        aggregation: measure.aggregation
      })),
      connections: (payload.connections || []).map(connection => ({
        connection_id: connection.connection_id,
        source_schema: connection.source_schema,
        source_table: connection.source_table,
        column_mapping: { ...connection.column_mapping }
      })),
      stages: (payload.stages || []).map(stage => ({
        dimensions: [...(stage.dimensions || [])],
        tolerances: (stage.tolerances || []).map(tolerance => ({
          measure_name: tolerance.measure_name,
          type: tolerance.type as ToleranceType,
          value: Number(tolerance.value)
        }))
      })),
      created_at: createdAt,
      updated_at: updatedAt,
      created_by: 'demo-user' as any,
      updated_by: 'demo-user' as any,
      is_soft_deleted: false,
      version: 1,
      encryption_key_name: 'demo-kms-key',
      encryption_key_version: 1,
      vault_key_version: 1,
      status: 'active'
    } as Job;

    (job as any).createdAt = createdAt.toISOString();
    (job as any).updatedAt = updatedAt.toISOString();

    if (job.connections?.length) {
      job.sourceConnectionId = job.connections[0]?.connection_id;
      job.targetConnectionId = job.connections[1]?.connection_id;
    }

    return job;
  }


  private createJobRun(job: any, overrides: Partial<DemoJobRun>): DemoJobRun {
    const now = new Date();
    return {
      id: overrides.id || this.generateId('run'),
      request_id: this.generateId('req'),
      job_id: job.id,
      workspace_id: job.workspace_id || 'ws-ecommerce',
      status: overrides.status || 'RUNNING',
      triggered_by: this.demoUser.email,
      run_label: overrides.run_label || 'Manual trigger',
      argo_workflow_name: overrides.argo_workflow_name || ('argo-' + this.generateId('wf')),
      started_at: overrides.started_at || (overrides.status === 'QUEUED' ? undefined : new Date(now.getTime() - 5 * 60 * 1000).toISOString()),
      completed_at: overrides.completed_at,
      rows_processed: overrides.rows_processed ?? 0,
      rows_matched: overrides.rows_matched ?? 0,
      rows_unmatched: overrides.rows_unmatched ?? 0,
      error: overrides.error,
      parameters: overrides.parameters || { window: 'yesterday' }
    };
  }

  private recordRun(jobId: string, run: DemoJobRun): void {
    const runs = this.jobRuns.get(jobId) || [];
    runs.unshift(run);
    this.jobRuns.set(jobId, runs);
    this.jobRunEvents.set(run.id, this.buildDemoEvents(run));
  }

  private buildDemoEvents(run: DemoJobRun): DemoJobEvent[] {
    const start = run.started_at ? new Date(run.started_at) : new Date();
    const events: DemoJobEvent[] = [
      this.buildEvent(run, 'JOB_STARTED', { timestamp: start.toISOString() }),
      this.buildEvent(run, 'ROW_PROGRESS', {
        rows_processed: run.rows_processed,
        rows_matched: run.rows_matched,
        rows_unmatched: run.rows_unmatched
      })
    ];

    if (run.status === 'COMPLETED') {
      events.push(this.buildEvent(run, 'JOB_COMPLETED', { completed_at: run.completed_at }));
    } else if (run.status === 'ERRORED') {
      events.push(this.buildEvent(run, 'JOB_FAILED', { error: run.error }));
    }

    return events;
  }

  private buildEvent(run: DemoJobRun, type: string, payload: Record<string, any> = {}): DemoJobEvent {
    return {
      id: this.generateId('evt'),
      job_id: run.job_id,
      run_id: run.id,
      event_type: type,
      payload,
      created_at: new Date().toISOString()
    };
  }

  handle(req: HttpRequest<any>): Observable<HttpEvent<any>> | null {
    if (!environment.demoMode) {
      return null;
    }

    const { method, body } = req;
    const url = req.url;
    const urlWithParams = req.urlWithParams;
    const pathname = new URL(urlWithParams, 'http://localhost').pathname;

    if (url.endsWith('/auth/login') && method === 'POST') {
      return this.ok({ success: true });
    }

    if (url.endsWith('/auth/me') && method === 'GET') {
      return this.ok(this.demoUser);
    }

    if (
      (url.endsWith('/auth/logout') && method === 'POST') ||
      (url.endsWith('/auth/refresh') && method === 'POST') ||
      (url.endsWith('/auth/logout-all') && method === 'POST')
    ) {
      return this.ok({});
    }

    if (url.endsWith('/auth/sessions') && method === 'GET') {
      return this.ok({ items: this.authSessions, pagination: this.buildPagination(this.authSessions.length) });
    }

    if (url.endsWith('/auth/device/sessions') && method === 'GET') {
      return this.ok({ items: this.deviceSessions, pagination: this.buildPagination(this.deviceSessions.length) });
    }

    if (url.endsWith('/workspace/egress-ips') && method === 'GET') {
      return this.ok({
        cellId: 'hef',
        configured: true,
        egressIps: ['203.0.113.10', '203.0.113.11'],
        updatedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()
      });
    }

    // ── Audit events (auditor-facing evidence surface) ──
    if (pathname.endsWith('/audit-events/exports') && method === 'POST') {
      const job = this.createDemoAuditExport(body ?? {});
      return this.ok({ export: job });
    }

    const auditExportDownloadMatch = pathname.match(/\/audit-events\/exports\/([^\/]+)\/download$/);
    if (auditExportDownloadMatch && method === 'GET') {
      return this.ok(new Blob(['{"demo":"audit export bundle"}\n'], { type: 'application/gzip' }));
    }

    const auditExportStatusMatch = pathname.match(/\/audit-events\/exports\/([^\/]+)$/);
    if (auditExportStatusMatch && method === 'GET') {
      const job = this.advanceDemoAuditExport(decodeURIComponent(auditExportStatusMatch[1]));
      return job ? this.ok({ export: job }) : this.notFound({ message: 'export not found' });
    }

    if (pathname.endsWith('/audit-events') && method === 'GET') {
      return this.ok(this.buildDemoAuditEventsResponse(req));
    }

    const auditEventDetailMatch = pathname.match(/\/audit-events\/([^\/]+)$/);
    if (auditEventDetailMatch && method === 'GET') {
      const id = decodeURIComponent(auditEventDetailMatch[1]);
      const entry = this.demoAuditEvents().find(e => e.id === id) ?? this.demoAuditEvents()[0];
      return entry ? this.ok(entry) : this.notFound({ message: 'event not found' });
    }

    const authSessionDeleteMatch = url.match(/\/auth\/sessions\/([^\/]+)$/);
    if (authSessionDeleteMatch && method === 'DELETE') {
      this.removeById(this.authSessions, 'id', decodeURIComponent(authSessionDeleteMatch[1]));
      return this.ok({});
    }

    const deviceSessionDeleteMatch = url.match(/\/auth\/device\/sessions\/([^\/]+)$/);
    if (deviceSessionDeleteMatch && method === 'DELETE') {
      this.removeById(this.deviceSessions, 'jti', decodeURIComponent(deviceSessionDeleteMatch[1]));
      return this.ok({});
    }

    if (url.endsWith('/workspaces') && method === 'GET') {
      return this.ok(this.buildWorkspaceResponse());
    }

    if (url.endsWith('/search') && method === 'GET') {
      const query = req.params.get('q') || '';
      const limit = Number(req.params.get('limit') || '8');
      return this.ok(this.buildCommandPaletteSearchResponse(query, limit));
    }

    if (url.endsWith('/connections/search') && method === 'GET') {
      const query = req.params.get('q') || '';
      const limit = Number(req.params.get('limit') || '8');
      return this.ok(this.buildConnectionSearchResponse(query, limit));
    }

    if (url.endsWith('/workspaces') && method === 'POST') {
      const now = new Date().toISOString();
      const workspace = {
        id: this.generateId('ws') as any,
        name: body?.name || `Workspace ${this.workspaces.length + 1}`,
        slug: body?.slug || this.slugify(body?.name || `workspace-${this.workspaces.length + 1}`),
        description: body?.description || '',
        settings: body?.settings || {},
        metadata: body?.metadata || {},
        created_at: now,
        updated_at: now,
        created_by: 'demo-user' as any,
        updated_by: 'demo-user' as any,
        version: 1
      };
      this.workspaces.push(workspace);
      return this.ok(this.wrap(workspace));
    }

    // Tenant billing/usage — current consumption vs entitlement limit per key.
    // `limit: null` is UNLIMITED. Mirrors the GET /billing/usage `TenantUsage` container
    // ({ metrics: TenantUsageMetric[] }) and exercises the ok / approaching / at-limit / unlimited states.
    if (url.endsWith('/billing/usage') && method === 'GET') {
      const scheduleCount = Array.from(this.jobSchedules.values()).reduce((total, schedules) => total + schedules.length, 0);
      return this.ok({ metrics: [
        { key: 'max_users', current: this.users.length, limit: 10 },
        { key: 'max_connections', current: this.connections.length, limit: 20 },
        { key: 'max_jobs', current: this.jobs.length, limit: 25 },
        { key: 'max_schedules', current: scheduleCount, limit: 20 },
        { key: 'max_api_keys', current: this.apiKeys.length, limit: 5 },
        { key: 'max_concurrent_runs', current: 1, limit: 3 },
        { key: 'max_runs_per_day', current: 47, limit: 100 },
        { key: 'max_runs_per_month', current: 612, limit: null },
        { key: 'min_schedule_interval_minutes', current: 15, limit: 15 },
        { key: 'max_storage_bytes', current: 1610612736, limit: 2147483648 },
        { key: 'max_api_rps', current: 8, limit: 10 },
        { key: 'max_concurrent_downloads', current: 0, limit: 2 },
      ] });
    }

    if (url.endsWith('/connections') && method === 'GET') {
      return this.ok(this.buildConnectionResponse());
    }

    if (url.endsWith('/connections') && method === 'POST') {
      const now = new Date().toISOString();
      const newConn = {
        id: this.generateId('conn'),
        name: body?.name || 'New Connection',
        type: body?.type || 'POSTGRES',
        description: body?.description,
        status: 'active',
        workspace_id: body?.workspace_id || 'ws-ecommerce',
        last_used_at: now,
        version: 1,
        created_at: now,
        updated_at: now,
        created_by: 'demo-user',
        updated_by: 'demo-user'
      };
      this.connections.push({
        summary: newConn,
        metadata: [{ name: body?.database || 'demo_database', schemas: cloneSchemas() }]
      });
      return this.ok({ ...newConn, data: newConn, status: 200, message: 'OK' });
    }

    if (url.endsWith('/connections/test') && method === 'POST') {
      return this.ok({ status: 'success', message: 'Connection successful' });
    }

    if (url.endsWith('/connections/excel-sharepoint/consent-url') && method === 'GET') {
      return this.ok({ consentUrl: `${window.location.origin}/connections/wizard?consent=success&entra_tenant_id=demo-entra-tenant` });
    }

    if (url.endsWith('/connections/excel-sharepoint/sites') && method === 'GET') {
      return this.ok({
        sites: [
          {
            id: 'site-finance-ops',
            displayName: 'Finance Operations',
            webUrl: 'https://contoso.sharepoint.com/sites/finance-operations'
          },
          {
            id: 'site-revenue-control',
            displayName: 'Revenue Control',
            webUrl: 'https://contoso.sharepoint.com/sites/revenue-control'
          },
          {
            id: 'site-ecommerce-recon',
            displayName: 'E-Commerce Reconciliation',
            webUrl: 'https://contoso.sharepoint.com/sites/ecommerce-reconciliation'
          }
        ]
      });
    }

    if (url.endsWith('/connections/stats') && method === 'GET') {
      return this.ok(this.buildConnectionStats());
    }

    // Single connection stats by ID — keep before the generic connection detail matcher.
    const connectionStatsMatch = url.match(/\/connections\/([^\/]+)\/stats$/);
    if (connectionStatsMatch && method === 'GET') {
      const connId = decodeURIComponent(connectionStatsMatch[1]);
      return this.ok(this.buildConnectionDetailStats(connId));
    }

    // Resume the job schedules that were auto-paused when this connection went stale
    // (connection-health-check). Refuses (409) while the connection is still stale; otherwise
    // clears the demo stale flag and reports one resumed + one still-paused (blocked by another
    // stale connection) so the partial-resumability UX is exercised.
    const resumeMatch = url.match(/\/connections\/([^\/]+)\/resume-paused-schedules$/);
    if (resumeMatch && method === 'POST') {
      const connId = decodeURIComponent(resumeMatch[1]);
      // DEMO SIMPLIFICATION: production core-api refuses (409) to resume while the connection is
      // still stale — the user must first fix it (e.g. re-enter credentials) and let the health
      // check pass. The demo has no separate credential-edit step, so a Resume click stands in for
      // "resolve + resume": we clear the stale flag and report a partial resume (one schedule
      // re-enabled, one still blocked by ANOTHER stale connection — the real partial-resumability
      // case). A second call is idempotent (nothing left to resume).
      if (this.recoveredStaleConnections.has(connId)) {
        return this.ok({ resumed: [], still_paused: [] });
      }
      this.recoveredStaleConnections.add(connId);
      return this.ok({
        resumed: ['sched-db2-ecommerce-nightly'],
        still_paused: [
          { schedule_id: 'sched-db2-ecommerce-hourly', blocked_by: ['conn-mysql-analytics'] }
        ]
      });
    }

    // Single connection detail — must come before the databases/schemas/tables/columns matchers
    const connectionDetailMatch = url.match(/\/connections\/([^\/]+)$/);
    if (connectionDetailMatch && method === 'GET' && !url.endsWith('/connections')) {
      const connId = decodeURIComponent(connectionDetailMatch[1]);
      // Skip sub-resource keywords
      if (!['test', 'stats'].includes(connId)) {
        const conn = this.findConnection(connId);
        if (conn) {
          const s = conn.summary;
          return this.ok({
            id: s.id,
            name: s.name,
            description: s.description,
            type: s.type,
            status: s.status,
            workspace_id: s.workspace_id,
            host: `${s.type.toLowerCase().replace('_', '-')}.internal.datarecs.io`,
            port: s.type === 'POSTGRES' ? 5432 : s.type === 'MYSQL' ? 3306 : s.type === 'SQL_SERVER' ? 1433 : s.type === 'DB2' ? 50000 : 1521,
            database: conn.metadata?.[0]?.name || 'default_db',
            username: 'datarecs_reader',
            ssl_enabled: true,
            connection_string_type: 'HOST_PORT',
            last_used_at: s.last_used_at,
            created_at: s.created_at,
            updated_at: s.updated_at,
            created_by: s.created_by,
            updated_by: s.updated_by,
            version: s.version,
          });
        }
        return this.notFound({ error: 'Connection not found' });
      }
    }

    const connectionJobsMatch = url.match(/\/connections\/([^\/]+)\/jobs$/);
    if (connectionJobsMatch && method === 'GET') {
      const connId = decodeURIComponent(connectionJobsMatch[1]);
      const items = this.jobs.filter(job =>
        (job.connections || []).some((connection: any) => connection.connection_id === connId)
      );
      return this.ok({ items, pagination: this.buildPagination(items.length) });
    }

    if (connectionDetailMatch && method === 'PUT') {
      const connId = decodeURIComponent(connectionDetailMatch[1]);
      const conn = this.findConnection(connId);
      if (!conn) {
        return this.notFound({ error: 'Connection not found' });
      }
      Object.assign(conn.summary, body, {
        id: conn.summary.id,
        updated_at: new Date().toISOString(),
        updated_by: 'demo-user',
        version: (conn.summary.version || 1) + 1
      });
      return this.ok(this.wrap(conn.summary));
    }

    if (connectionDetailMatch && method === 'DELETE') {
      const connId = decodeURIComponent(connectionDetailMatch[1]);
      const idx = this.connections.findIndex(conn => conn.summary.id === connId);
      if (idx >= 0) {
        this.connections.splice(idx, 1);
      }
      return this.ok(this.wrap(undefined as void));
    }

    const databasesMatch = url.match(/\/connections\/(.+)\/databases$/);
    if (databasesMatch && method === 'GET') {
      const conn = this.findConnection(databasesMatch[1]);
      return this.ok({ databases: conn ? conn.metadata.map(db => db.name) : [] });
    }

    const schemasMatch = url.match(/\/connections\/(.+)\/databases\/(.+)\/schemas$/);
    if (schemasMatch && method === 'GET') {
      const conn = this.findConnection(schemasMatch[1]);
      const database = conn?.metadata.find(db => db.name === decodeURIComponent(schemasMatch[2]));
      return this.ok({ schemas: database ? database.schemas.map(s => s.name) : [] });
    }

    const tablesMatch = url.match(/\/connections\/(.+)\/databases\/(.+)\/schemas\/(.+)\/tables$/);
    if (tablesMatch && method === 'GET') {
      const database = this.getDatabase(tablesMatch[1], tablesMatch[2]);
      const schema = database?.schemas.find(s => s.name === decodeURIComponent(tablesMatch[3]));
      return this.ok({ tables: schema ? schema.tables.map(t => t.name) : [] });
    }

    const columnsMatch = url.match(/\/connections\/(.+)\/databases\/(.+)\/schemas\/(.+)\/tables\/(.+)\/columns$/);
    if (columnsMatch && method === 'GET') {
      const table = this.getTable(columnsMatch[1], columnsMatch[2], columnsMatch[3], columnsMatch[4]);
      return this.ok({ columns: table ? table.columns : [] });
    }

    if (url.endsWith('/job-groups') && method === 'GET') {
      return this.ok(this.wrap(this.getJobGroups()));
    }

    const jobGroupDetailMatch = url.match(/\/job-groups\/([^\/]+)$/);
    if (jobGroupDetailMatch && method === 'GET') {
      const groupId = decodeURIComponent(jobGroupDetailMatch[1]);
      const group = this.getJobGroups().find(g => g.id === groupId);
      return this.ok(this.wrap(group));
    }
    if (jobGroupDetailMatch && (method === 'PATCH' || method === 'PUT')) {
      const groupId = decodeURIComponent(jobGroupDetailMatch[1]);
      const group = this.jobGroups.find(g => g.id === groupId);
      if (group) {
        Object.assign(group, body, { updated_at: new Date().toISOString() });
        if (body?.job_ids) {
          group.job_ids = body.job_ids;
          group.jobs = body.job_ids
            .map((jobId: string) => this.getJobById(jobId))
            .filter(Boolean);
        }
      }
      return this.ok(this.wrap(group));
    }

    if (jobGroupDetailMatch && method === 'DELETE') {
      const groupId = decodeURIComponent(jobGroupDetailMatch[1]);
      const idx = this.jobGroups.findIndex(g => g.id === groupId);
      if (idx >= 0) {
        this.jobGroups.splice(idx, 1);
      }
      for (const job of this.jobs) {
        if (job.parent_job_group_id === groupId || job.job_group_id === groupId) {
          job.parent_job_group_id = undefined;
          job.job_group_id = undefined;
        }
      }
      return this.ok(this.wrap(undefined as void));
    }

    if (url.endsWith('/job-groups') && method === 'POST') {
      const selectedJobs = (body?.job_ids || [])
        .map((jobId: string) => this.getJobById(jobId))
        .filter(Boolean);
      const newGroup: any = {
        ...body,
        id: this.generateId('jg') as any,
        jobs: selectedJobs,
        job_ids: body?.job_ids || selectedJobs.map((job: any) => job.id),
        workspace_id: body.workspace_id || 'ws-ecommerce',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        created_by: 'demo-user' as any,
        updated_by: 'demo-user' as any,
        version: 1
      };
      this.jobGroups.push(newGroup);
      return this.ok(this.wrap(newGroup));
    }

    if (url.endsWith('/jobs') && method === 'GET') {
      return this.ok({ items: this.jobs, pagination: this.buildPagination(this.jobs.length) });
    }

    if (url.endsWith('/jobs/stats') && method === 'GET') {
      return this.ok(this.buildJobStats());
    }

    if (url.includes('/job-runs') && method === 'GET') {
      const params = new URL(urlWithParams, 'http://localhost').searchParams;
      const status = params.get('status');
      const workspaceId = params.get('workspaceId');
      let runs = this.getAllRuns();
      if (status) {
        runs = runs.filter(run => run.status === status);
      }
      if (workspaceId) {
        runs = runs.filter(run => run.workspace_id === workspaceId);
      }
      runs = runs.sort((a, b) => (b.started_at || b.completed_at || '').localeCompare(a.started_at || a.completed_at || ''));
      return this.ok({ items: runs, pagination: this.buildPagination(runs.length) });
    }

    if (url.endsWith('/jobs') && method === 'POST') {
      const payload = body as CreateJobPayload;
      const job = this.createJobFromPayload(payload, body?.description);
      if (body?.parent_job_group_id) {
        (job as any).parent_job_group_id = body.parent_job_group_id;
        this.attachJobToGroup(body.parent_job_group_id, job);
      }
      this.jobs.push(job);
      return this.ok(this.wrap(job));
    }
    const triggerMatch = url.match(/\/jobs\/([^\/]+)\/runs\/trigger$/);
    if (triggerMatch && method === 'POST') {
      const jobId = decodeURIComponent(triggerMatch[1]);
      const job = this.getJobById(jobId);
      if (!job) {
        return this.ok({});
      }
      const run = this.createJobRun(job, {
        run_label: body?.run_label,
        parameters: body?.params,
        status: body?.mode === 'DRY_RUN' ? 'COMPLETED' : 'QUEUED'
      });
      this.recordRun(jobId, run);
      if (run.status === 'QUEUED') {
        this.simulateRunLifecycle(jobId, run);
      }
      return this.ok({
        run_id: run.id,
        job_id: jobId,
        status: run.status,
        tracking_url: 'https://argo.local/workflows/' + run.argo_workflow_name,
        created_at: new Date().toISOString()
      });
    }

    const runsListMatch = url.match(/\/jobs\/([^\/]+)\/runs$/);
    if (runsListMatch && method === 'GET') {
      const jobId = decodeURIComponent(runsListMatch[1]);
      const runs = this.getJobRuns(jobId);
      return this.ok({ items: runs, pagination: this.buildPagination(runs.length) });
    }

    const runDetailMatch = url.match(/\/jobs\/([^\/]+)\/runs\/([^\/]+)$/);
    if (runDetailMatch && method === 'GET') {
      const jobId = decodeURIComponent(runDetailMatch[1]);
      const runId = decodeURIComponent(runDetailMatch[2]);
      const run = this.findRun(jobId, runId);
      return this.ok(run || {});
    }

    const runResultsSummaryMatch = pathname.match(/\/jobs\/([^\/]+)\/runs\/([^\/]+)\/results\/summary$/);
    if (runResultsSummaryMatch && method === 'GET') {
      const jobId = decodeURIComponent(runResultsSummaryMatch[1]);
      const runId = decodeURIComponent(runResultsSummaryMatch[2]);
      return this.ok(this.buildRunResultsSummary(jobId, runId));
    }

    const runMismatchRowsMatch = pathname.match(/\/jobs\/([^\/]+)\/runs\/([^\/]+)\/results\/mismatches$/);
    if (runMismatchRowsMatch && method === 'GET') {
      return this.ok(this.buildMismatchRows(new URL(urlWithParams, 'http://localhost').searchParams));
    }

    const runResultsDownloadMatch = pathname.match(/\/jobs\/([^\/]+)\/runs\/([^\/]+)\/results\/download$/);
    if (runResultsDownloadMatch && method === 'GET') {
      const params = new URL(urlWithParams, 'http://localhost').searchParams;
      const format = params.get('format') || 'csv';
      const content = format === 'json'
        ? JSON.stringify({ rows: this.buildMismatchRows(params).items }, null, 2)
        : 'order_id,source_a_total,source_b_total,difference\nORD-1001,1210.42,1204.42,6\n';
      return of(new HttpResponse({
        status: 200,
        body: new Blob([content], { type: format === 'json' ? 'application/json' : 'text/csv' })
      }));
    }

    const cancelMatch = url.match(/\/jobs\/([^\/]+)\/runs\/([^\/]+)\/cancel$/);
    if (cancelMatch && method === 'POST') {
      const jobId = decodeURIComponent(cancelMatch[1]);
      const runId = decodeURIComponent(cancelMatch[2]);
      const run = this.findRun(jobId, runId);
      if (run && (run.status === 'RUNNING' || run.status === 'QUEUED')) {
        run.status = 'CANCELLED';
        run.completed_at = new Date().toISOString();
        const events = this.jobRunEvents.get(run.id) || [];
        events.push(this.buildEvent(run, 'JOB_CANCELLED', { reason: body?.reason }));
        this.jobRunEvents.set(run.id, events);
      }
      return this.ok({});
    }

    const eventsMatch = url.match(/\/jobs\/([^\/]+)\/runs\/([^\/]+)\/events$/);
    if (eventsMatch && method === 'GET') {
      const runId = decodeURIComponent(eventsMatch[2]);
      // Bare array, not a paginated envelope — matches the real endpoint contract
      // (see job-runs.service.ts listEvents()). Wrapping this broke `.slice()` in
      // job-run-detail.component.ts, leaving the Event stream panel stuck on "Loading events".
      const events = this.jobRunEvents.get(runId) || [];
      return this.ok(events);
    }

    const schedulesMatch = url.match(/\/jobs\/([^\/]+)\/schedules$/);
    if (schedulesMatch && method === 'GET') {
      const jobId = decodeURIComponent(schedulesMatch[1]);
      const schedules = this.jobSchedules.get(jobId) || [];
      return this.ok(schedules);
    }

    if (schedulesMatch && method === 'POST') {
      const jobId = decodeURIComponent(schedulesMatch[1]);
      const schedules = this.jobSchedules.get(jobId) || [];
      const schedule: DemoJobSchedule = {
        schedule_id: this.generateId('sched'),
        job_id: jobId,
        cron_expression: body?.cron_expression,
        timezone: body?.timezone || 'UTC',
        enabled: body?.enabled ?? true,
        params: body?.params,
        next_run_at: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
        created_at: new Date().toISOString()
      };
      schedules.push(schedule);
      this.jobSchedules.set(jobId, schedules);
      return this.ok(schedule);
    }

    const scheduleDetailMatch = url.match(/\/jobs\/([^\/]+)\/schedules\/([^\/]+)$/);
    if (scheduleDetailMatch && method === 'PATCH') {
      const jobId = decodeURIComponent(scheduleDetailMatch[1]);
      const scheduleId = decodeURIComponent(scheduleDetailMatch[2]);
      const schedules = this.jobSchedules.get(jobId) || [];
      const schedule = schedules.find(s => s.schedule_id === scheduleId);
      if (schedule) {
        Object.assign(schedule, body, { updated_at: new Date().toISOString() });
      }
      return this.ok(schedule || {});
    }

    if (scheduleDetailMatch && method === 'DELETE') {
      const jobId = decodeURIComponent(scheduleDetailMatch[1]);
      const scheduleId = decodeURIComponent(scheduleDetailMatch[2]);
      const schedules = this.jobSchedules.get(jobId) || [];
      const idx = schedules.findIndex(s => s.schedule_id === scheduleId);
      if (idx >= 0) {
        schedules.splice(idx, 1);
      }
      this.jobSchedules.set(jobId, schedules);
      return this.ok({});
    }

    const jobDetailMatch = url.match(/\/jobs\/([^\/]+)$/);
    if (jobDetailMatch && method === 'GET') {
      const job = this.getJobById(decodeURIComponent(jobDetailMatch[1]));
      return this.ok(this.wrap(job));
    }
    if (jobDetailMatch && method === 'PUT') {
      const jobId = decodeURIComponent(jobDetailMatch[1]);
      const job = this.getJobById(jobId);
      if (!job) {
        return this.ok(this.wrap({}));
      }
      const payload = body || {};
      if (payload.name !== undefined) {
        job.name = payload.name;
      }
      if (payload.description !== undefined) {
        job.description = payload.description;
      }
      if (Array.isArray(payload.dimensions)) {
        job.dimensions = [...payload.dimensions];
      }
      if (Array.isArray(payload.measures)) {
        job.measures = payload.measures.map((measure: any) => ({
          name: measure.name,
          aggregation: measure.aggregation
        }));
      }
      if (Array.isArray(payload.stages)) {
        job.stages = payload.stages.map((stage: any) => ({
          dimensions: Array.isArray(stage.dimensions) ? [...stage.dimensions] : [],
          tolerances: Array.isArray(stage.tolerances)
            ? stage.tolerances.map((tol: any) => ({
                measure_name: tol.measure_name,
                type: tol.type,
                value: Number(tol.value)
              }))
            : []
        }));
      }
      const updatedAt = new Date();
      job.updated_at = updatedAt;
      (job as any).updatedAt = updatedAt.toISOString();
      return this.ok(this.wrap(job));
    }

    if (jobDetailMatch && method === 'DELETE') {
      const jobId = decodeURIComponent(jobDetailMatch[1]);
      const idx = this.jobs.findIndex(job => job.id === jobId);
      if (idx >= 0) {
        this.jobs.splice(idx, 1);
      }
      for (const group of this.jobGroups) {
        group.job_ids = (group.job_ids || []).filter((id: string) => id !== jobId);
        group.jobs = (group.jobs || []).filter((job: any) => job.id !== jobId);
      }
      this.jobRuns.delete(jobId);
      return this.ok(this.wrap(undefined as void));
    }



    if (url.includes('/job-groups/') && url.endsWith('/jobs') && method === 'POST') {
      const jobGroupId = url.split('/job-groups/')[1].split('/')[0];
      const job = {
        ...body,
        id: this.generateId('job') as any,
        parent_job_group_id: jobGroupId,
        workspace_id: body.workspace_id || 'ws-ecommerce',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        created_by: 'demo-user' as any,
        updated_by: 'demo-user' as any,
        version: 1
      };
      this.jobs.push(job);
      this.attachJobToGroup(jobGroupId, job);
      return this.ok(this.wrap(job));
    }

    if (url.endsWith('/api-keys') && method === 'GET') {
      return this.ok({
        items: this.apiKeys,
        pagination: this.buildPagination(this.apiKeys.length),
        isAllApiKeys: true
      });
    }

    if (url.endsWith('/api-keys') && method === 'POST') {
      const apiKey = {
        ...body,
        id: this.generateId('key'),
        prefix: body?.prefix || `drk_demo_${this.generateId('pk')}`.slice(0, 12),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        created_by: 'demo-user',
        updated_by: 'demo-user',
        permissions: body?.permissions || ['list_jobs'],
        scopes: body?.scopes || ['tenant:read']
      };
      this.apiKeys.unshift(apiKey);
      return this.ok({ apiKeyDetails: apiKey, cleartextApiKey: `${apiKey.prefix}.${this.generateId('keyval')}` });
    }

    const apiKeyMatch = url.match(/\/api-keys\/([^\/]+)$/);
    if (apiKeyMatch && method === 'PUT') {
      const id = decodeURIComponent(apiKeyMatch[1]);
      const existing = this.apiKeys.find(k => k.id === id);
      if (existing) {
        Object.assign(existing, body, { updated_at: new Date().toISOString() });
      }
      return this.ok(existing || {});
    }

    if (apiKeyMatch && method === 'DELETE') {
      const id = decodeURIComponent(apiKeyMatch[1]);
      const idx = this.apiKeys.findIndex(k => k.id === id);
      if (idx >= 0) { this.apiKeys.splice(idx, 1); }
      return this.ok({});
    }

    if (url.endsWith('/users') && method === 'GET') {
      return this.ok(this.users);
    }

    if (url.endsWith('/users/invite') && method === 'POST') {
      const invitation = this.createInvitation(body?.email, body?.roles);
      return this.ok({ invitation: this.serializeInvitation(invitation) });
    }

    if (url.endsWith('/users/invitations') && method === 'GET') {
      return this.ok(this.invitations.map(invite => this.serializeInvitation(invite)));
    }

    const cancelInvitationMatch = url.match(/\/users\/invitations\/([^\/]+)$/);
    if (cancelInvitationMatch && method === 'DELETE') {
      this.cancelInvitation(decodeURIComponent(cancelInvitationMatch[1]));
      return this.ok({});
    }

    if (url.endsWith('/users/requests') && method === 'GET') {
      return this.ok(this.joinRequests);
    }

    const approveRequestMatch = url.match(/\/users\/requests\/([^\/]+)\/approve$/);
    if (approveRequestMatch && method === 'POST') {
      this.approveJoinRequest(decodeURIComponent(approveRequestMatch[1]));
      return this.ok({});
    }

    const denyRequestMatch = url.match(/\/users\/requests\/([^\/]+)\/deny$/);
    if (denyRequestMatch && method === 'POST') {
      this.denyJoinRequest(decodeURIComponent(denyRequestMatch[1]));
      return this.ok({});
    }

    if (url.endsWith('/users/stats') && method === 'GET') {
      return this.ok(this.computeUserStats());
    }

    const validateImpersonationMatch = url.match(/\/users\/([^\/]+)\/validate-impersonation$/);
    if (validateImpersonationMatch && method === 'POST') {
      return this.ok({ status: 'validated' });
    }

    const userPermissionsMatch = url.match(/\/users\/([^\/]+)\/permissions$/);
    if (userPermissionsMatch && method === 'GET') {
      const user = this.findUser(decodeURIComponent(userPermissionsMatch[1]));
      const permissions = user ? this.buildUserPermissions(user) : [];
      return this.ok(permissions);
    }

    const deleteUserMatch = url.match(/\/users\/([^\/]+)$/);
    if (deleteUserMatch && method === 'DELETE') {
      this.removeUser(decodeURIComponent(deleteUserMatch[1]));
      return this.ok({});
    }

    if (url.endsWith('/admin/users') && method === 'GET') {
      return this.ok(this.wrap(this.users));
    }

    if (url.endsWith('/admin/users/invite') && method === 'POST') {
      const invitation = this.createInvitation(body?.email, body?.roles);
      const invitedUser = this.findUser(invitation.user_id);
      return this.ok(this.wrap(invitedUser));
    }

    if (url.endsWith('/admin/billing') && method === 'GET') {
      return this.ok(this.wrap({ plan: 'demo', status: 'active', seats: { used: this.users.length, limit: 25 } }));
    }

    if (url.endsWith('/admin/verify-email') && method === 'POST') {
      return this.ok(this.wrap({ status: 'verified' }));
    }

    if (url.endsWith('/permissions') && method === 'GET') {
      const payload = {
        items: this.permissions,
        pagination: this.buildPagination(this.permissions.length)
      };
      return this.ok(payload);
    }

    if (url.endsWith('/roles/platform') && method === 'GET') {
      const enrichedRoles = this.platformRoles.map(role => {
        const assignments = this.roleAssignments.get(role.name) || [];
        return { ...role, usage_count: assignments.length };
      });
      return this.ok(enrichedRoles);
    }

    const customRoleIdMatch = url.match(/\/roles\/custom\/(\d+)$/);
    if (customRoleIdMatch && method === 'GET') {
      const roleId = parseInt(customRoleIdMatch[1], 10);
      const role = this.customRoles.find(r => r.id === roleId);
      return this.ok(role || {});
    }

    if (url.endsWith('/roles/custom') && method === 'GET') {
      const enrichedRoles = this.customRoles.map(role => {
        const assignments = this.roleAssignments.get(role.id.toString()) || this.roleAssignments.get(role.name) || [];
        return { ...role, usage_count: assignments.length };
      });
      return this.ok(enrichedRoles);
    }

    if (url.endsWith('/roles/custom') && method === 'PUT') {
      const roleId = body?.id;
      const existing = this.customRoles.find(r => r.id === roleId);
      if (existing) {
        existing.name = body?.name ?? existing.name;
        existing.scope = body?.scope ?? existing.scope;
        existing.description = body?.description ?? existing.description;
        existing.permissions = body?.permissions ?? existing.permissions;
      }
      return this.ok(existing || {});
    }

    if (url.endsWith('/roles/custom') && method === 'POST') {
      const newRole = {
        id: this.customRoles.length ? Math.max(...this.customRoles.map(r => r.id)) + 1 : 2000,
        name: body?.name || `custom_role_${this.customRoles.length + 1}`,
        scope: body?.scope || 'tenant',
        description: body?.description,
        permissions: body?.permissions || []
      };
      this.customRoles.push(newRole);
      return this.ok(newRole);
    }

    if (url.endsWith('/groups') && method === 'GET') {
      return this.ok(this.groups);
    }

    if (url.endsWith('/groups') && method === 'POST') {
      const newGroup: DemoGroup = {
        id: this.generateId('group'),
        name: body?.name || `Group ${this.groups.length + 1}`,
        description: body?.description,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      this.groups.push(newGroup);
      this.groupMembers.set(newGroup.id, []);
      return this.ok(newGroup);
    }

    const groupMatch = url.match(/\/groups\/([^\/]+)$/);
    if (groupMatch && method === 'GET') {
      const group = this.findGroup(decodeURIComponent(groupMatch[1]));
      return this.ok(group || {});
    }

    if (groupMatch && method === 'PUT') {
      const groupId = decodeURIComponent(groupMatch[1]);
      const group = this.findGroup(groupId);
      if (group) {
        group.name = body?.name ?? group.name;
        group.description = body?.description ?? group.description;
        group.updated_at = new Date().toISOString();
      }
      return this.ok(group || {});
    }

    if (groupMatch && method === 'DELETE') {
      const groupId = decodeURIComponent(groupMatch[1]);
      const idx = this.groups.findIndex(entry => entry.id === groupId);
      if (idx >= 0) {
        this.groups.splice(idx, 1);
        this.groupMembers.delete(groupId);
      }
      return this.ok({});
    }

    const groupMembersMatch = url.match(/\/groups\/([^\/]+)\/members$/);
    if (groupMembersMatch && method === 'GET') {
      const groupId = decodeURIComponent(groupMembersMatch[1]);
      return this.ok(this.getGroupMembersList(groupId));
    }

    if (groupMembersMatch && method === 'POST') {
      const groupId = decodeURIComponent(groupMembersMatch[1]);
      const userId = body?.userId || body?.user_id;
      const member = this.addGroupMember(groupId, userId);
      return this.ok(member || {});
    }

    const groupMemberDeleteMatch = url.match(/\/groups\/([^\/]+)\/members\/([^\/]+)$/);
    if (groupMemberDeleteMatch && method === 'DELETE') {
      const groupId = decodeURIComponent(groupMemberDeleteMatch[1]);
      const userId = decodeURIComponent(groupMemberDeleteMatch[2]);
      this.removeGroupMember(groupId, userId);
      return this.ok({});
    }

    const roleAssignmentsMatch = url.match(/\/roles\/([^\/]+)\/assignments$/);
    if (roleAssignmentsMatch && method === 'GET') {
      let roleKey = decodeURIComponent(roleAssignmentsMatch[1]);
      
      // If the ID is a number, it might be a platform role ID. Map it to the name.
      if (!isNaN(Number(roleKey))) {
        const platformRole = this.platformRoles.find(r => r.id === Number(roleKey));
        if (platformRole) {
          roleKey = platformRole.name;
        }
      }

      const rawAssignments = this.roleAssignments.get(roleKey) || [];

      const enrichedAssignments = rawAssignments.map(assignment => {
        if (assignment.subject_type === 'user') {
          const user = this.findUser(assignment.subject);
          return {
            ...assignment,
            user_first_name: user?.first_name || 'Unknown',
            user_last_name: user?.last_name || 'User',
            user_email: user?.email || 'unknown@datarecs.local'
          };
        }
        return assignment;
      });

      return this.ok(enrichedAssignments);
    }
    // Role Bindings CRUD
    const roleBindingIdMatch = url.match(/\/role-bindings\/(\d+)$/);

    if (roleBindingIdMatch && method === 'GET') {
      const id = roleBindingIdMatch[1];
      const binding = this.roleBindings.find(rb => rb.id === id);
      return this.ok(binding || {});
    }

    if (roleBindingIdMatch && method === 'PUT') {
      const id = roleBindingIdMatch[1];
      const binding = this.roleBindings.find(rb => rb.id === id);
      if (!binding) {
        return this.notFound({ error: 'Role binding not found' });
      }
      Object.assign(binding, body, { id });
      return this.ok(binding);
    }

    if (roleBindingIdMatch && method === 'DELETE') {
      const id = roleBindingIdMatch[1];
      const idx = this.roleBindings.findIndex(rb => rb.id === id);
      if (idx === -1) {
        return this.notFound({ error: 'Role binding not found' });
      }
      this.roleBindings.splice(idx, 1);
      return this.ok({});
    }

    if (url.endsWith('/sso/connection') && method === 'GET') {
      return this.ssoConnection
        ? this.ok(this.ssoConnection)
        : this.notFound({ message: 'SSO connection is not configured' });
    }

    if (url.endsWith('/sso/connection') && method === 'POST') {
      this.ssoConnection = {
        ...body,
        id: 'sso-demo-connection',
        status: 'active',
        provider: body?.provider || 'oidc',
        scim_enabled: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      return this.ok(this.ssoConnection);
    }

    if (url.endsWith('/sso/connection') && method === 'DELETE') {
      this.ssoConnection = null;
      return this.ok({});
    }

    if (url.endsWith('/sso/connection/enable-scim') && method === 'POST') {
      this.ensureSsoConnection();
      this.ssoConnection.scim_enabled = true;
      return this.ok({ scim_token: `scim_demo_${this.generateId('token')}` });
    }

    if (url.endsWith('/sso/connection/disable-scim') && method === 'POST') {
      this.ensureSsoConnection();
      this.ssoConnection.scim_enabled = false;
      return this.ok({});
    }

    if (url.endsWith('/sso/connection/rotate-scim-token') && method === 'POST') {
      this.ensureSsoConnection();
      return this.ok({ scim_token: `scim_demo_${this.generateId('token')}` });
    }

    if (url.endsWith('/sso/connection/test-login') && method === 'POST') {
      return this.ok({ authorization_url: '/auth/demo-sso' });
    }

    if (url.endsWith('/sso/connection/migrate') && method === 'POST') {
      this.ssoMigrationStatus = 'completed';
      return this.ok({ migration_id: 'sso-migration-demo' });
    }

    if (url.endsWith('/sso/connection/migration-status') && method === 'GET') {
      return this.ok({ status: this.ssoMigrationStatus, migrated_user_count: this.users.length });
    }

    const bypassSsoMatch = url.match(/\/users\/([^\/]+)\/bypass-sso$/);
    if (bypassSsoMatch && method === 'PATCH') {
      const user = this.findUser(decodeURIComponent(bypassSsoMatch[1])) as any;
      if (user) {
        user.bypass_sso = Boolean(body?.bypass_sso);
      }
      return this.ok({});
    }

    const resetIdentityMatch = url.match(/\/users\/([^\/]+)\/reset-identity$/);
    if (resetIdentityMatch && method === 'POST') {
      const user = this.findUser(decodeURIComponent(resetIdentityMatch[1])) as any;
      if (user) {
        user.provisioned_by = 'manual';
        user.idp_subject = undefined;
        if (body?.clear_external_id) {
          user.external_id = undefined;
        }
      }
      return this.ok({});
    }

    const archiveUserMatch = url.match(/\/users\/([^\/]+)\/archive$/);
    if (archiveUserMatch && method === 'POST') {
      const user = this.findUser(decodeURIComponent(archiveUserMatch[1])) as any;
      if (user) {
        user.is_soft_deleted = true;
        user.status = 'disabled';
      }
      return this.ok({});
    }

    if (url.endsWith('/role-bindings') && method === 'POST') {
      const binding = {
        ...body,
        // #63 model drift: RoleBinding ids are UUID strings; demo keeps a numeric
        // counter but stores ids as strings so lookups (string route params) match.
        id: String(this.nextRoleBindingId++),
      };
      this.roleBindings.push(binding);
      return this.ok(binding);
    }

    if (url.endsWith('/role-bindings') && method === 'GET') {
      return this.ok({
        items: this.roleBindings,
        pagination: this.buildPagination(this.roleBindings.length)
      });
    }

    if (url.endsWith('/tenant-encryption/config') && method === 'GET') {
      const isByok = this.kekData.ownership === 'byok';
      return this.ok({
        encryption_mode: isByok ? 'BYOK' : 'MANAGED',
        byok_migration_status: 'not_started',
        byok_key_name: isByok ? this.kekData.name : null,
        byok_kms_key_uri: isByok ? this.kekData.residence : null,
        byok_migration_completed_at: isByok ? this.kekData.last_rotated : null,
      });
    }

    if (url.endsWith('/tenant-encryption/config') && method === 'POST') {
      this.updateKekOwnership('byok', {
        name: body?.kms_key_uri || body?.key_name || 'projects/demo/locations/europe-west2/keyRings/datarecs/cryptoKeys/demo',
        residence: body?.kms_key_uri || body?.location || 'GCP KMS europe-west2'
      });
      // ByokMigrationService.startMigration() begins polling GET /tenant-encryption/migration-status
      // right after this call resolves — mark the migration in flight so that poll has real
      // (briefly "migrating" before "completed") state to report instead of leaking to the network.
      this.byokMigration = { active: true, polls: 0 };
      return this.ok({
        encryption_mode: 'BYOK',
        byok_migration_status: 'in_progress',
        byok_key_name: this.kekData.name,
        byok_kms_key_uri: this.kekData.residence,
        byok_migration_completed_at: null
      });
    }

    if (url.endsWith('/tenant-encryption/revert') && method === 'POST') {
      this.byokMigration = { active: true, polls: 0 };
      this.updateKekOwnership('managed');
      return this.ok({ encryption_mode: 'MANAGED', byok_migration_status: 'not_started' });
    }

    // Polled every 5s by ByokMigrationService while `byokMigration.active` (set above) —
    // advances to 'completed' after a couple of polls so the migrating/rolling_back banner
    // on keys-list.component.ts is visible briefly, matching the BYOS migration-status handler.
    if (url.endsWith('/tenant-encryption/migration-status') && method === 'GET') {
      if (!this.byokMigration.active) {
        return this.ok({ status: 'completed' });
      }
      this.byokMigration.polls += 1;
      const done = this.byokMigration.polls >= 2;
      if (done) {
        this.byokMigration.active = false;
      }
      return this.ok({ status: done ? 'completed' : 'in_progress' });
    }

    if (url.endsWith('/tenant-encryption/test-access') && method === 'POST') {
      return this.ok({ success: true, message: 'Service account can access the KMS key' });
    }

    if (url.endsWith('/keys') && method === 'GET') {
      const keysResponse: DemoEncryptionKeyResponse = {
        kek: { ...this.kekData },
        deks: [
          { name: 'connection-credentials-dek', purpose: 'Connection credentials encryption', last_rotated: daysAgoIso(30), rotation_frequency: 'Every 90 days' },
          { name: 'job-results-dek', purpose: 'Job result data encryption', last_rotated: daysAgoIso(60), rotation_frequency: 'Every 90 days' },
          { name: 'audit-log-dek', purpose: 'Audit log encryption', last_rotated: daysAgoIso(15), rotation_frequency: 'Every 30 days' }
        ]
      };
      return this.ok(keysResponse);
    }

    if (url.endsWith('/keys/test-access') && method === 'POST') {
      return of(null).pipe(
        delay(3000),
        map(() => new HttpResponse({
          status: 200,
          body: { success: true, message: 'Service account can access the KMS key' }
        }))
      );
    }

    if (url.endsWith('/data-storage') && method === 'GET') {
      return this.ok({ storage: { ...this.storageData } });
    }

    if (url.endsWith('/data-storage/test-permissions') && method === 'POST') {
      return of(null).pipe(
        delay(2500),
        map(() => new HttpResponse({
          status: 200,
          body: { success: true, message: 'Service account has the required permissions on the bucket' }
        }))
      );
    }

    if (url.endsWith('/data-storage/config') && method === 'POST') {
      this.updateStorageOwnership('byos', {
        bucketName: body?.bucket_name || body?.bucketName || 'demo-customer-reconciliation-artifacts',
        gcpProject: body?.gcp_project || body?.gcpProject || 'customer-demo-project',
        region: body?.region || 'europe-west2'
      });
      return this.ok({ storage: { ...this.storageData } });
    }

    if (url.endsWith('/data-storage/migrate') && method === 'POST') {
      this.byosMigration = { active: true, polls: 0 };
      return this.ok({ migration_id: 'storage-migration-demo', status: 'in_progress' });
    }

    if (url.endsWith('/data-storage/revert') && method === 'POST') {
      this.byosMigration = { active: true, polls: 0 };
      this.updateStorageOwnership('managed');
      return this.ok({ storage: { ...this.storageData } });
    }

    // Polled every 5s by ByosMigrationService while a BYOS migration/revert is in flight —
    // `byosMigration.active` is set by the /migrate and /revert handlers above. Advances
    // through the same phases the real backend reports so the progress bar + banner in
    // data-storage-list.component.ts / artifact-posture-panel.component.ts have real state
    // to render instead of a leaked call to the network.
    if (url.endsWith('/data-storage/migration-status') && method === 'GET') {
      if (!this.byosMigration.active) {
        return this.ok({ byos_migration_status: 'not_started' });
      }
      this.byosMigration.polls += 1;
      const done = this.byosMigration.polls >= 3;
      if (done) {
        this.byosMigration.active = false;
      }
      return this.ok({
        byos_migration_status: done ? 'completed' : 'in_progress',
        byos_migration_started_at: minutesAgoIso(1),
        byos_migration_completed_at: done ? new Date().toISOString() : undefined
      });
    }

    if (url.endsWith('/data-storage/migration-progress') && method === 'GET') {
      const totalObjects = 480;
      const totalBytes = 1_310_720_000;
      const pollsForProgress = Math.min(this.byosMigration.polls, 3);
      const percent = Math.min(100, Math.round((pollsForProgress / 3) * 100));
      const phase = percent >= 100 ? 'verifying' : percent > 0 ? 'copying' : 'preparing';
      return this.ok({
        phase,
        objects_copied: Math.round((percent / 100) * totalObjects),
        objects_total: totalObjects,
        bytes_copied: Math.round((percent / 100) * totalBytes),
        bytes_total: totalBytes,
        percent_complete: percent,
        started_at: minutesAgoIso(1)
      });
    }

    const workspaceMatch = url.match(/\/workspaces\/([^\/]+)$/);
    if (workspaceMatch && method === 'GET') {
      const workspaceId = decodeURIComponent(workspaceMatch[1]);
      const ws = this.workspaces.find((w) => w.id === workspaceId);
      return this.ok(this.wrap(ws || {}));
    }

    if (workspaceMatch && method === 'PUT') {
      const workspaceId = decodeURIComponent(workspaceMatch[1]);
      const ws = this.workspaces.find((w) => w.id === workspaceId);
      if (ws) {
        Object.assign(ws, body);
        ws.updated_at = new Date().toISOString();
      }
      return this.ok(this.wrap(ws || {}));
    }

    if (workspaceMatch && method === 'DELETE') {
      const workspaceId = decodeURIComponent(workspaceMatch[1]);
      const idx = this.workspaces.findIndex((w) => w.id === workspaceId);
      if (idx >= 0) {
        this.workspaces.splice(idx, 1);
      }
      return this.ok({});
    }

    // Audit Log
    if (url.includes('/audit-log') && method === 'GET') {
      const parsedUrl = new URL(urlWithParams, 'http://localhost');
      const typeFilter = parsedUrl.searchParams.get('type');
      const searchFilter = parsedUrl.searchParams.get('search');
      const fromFilter = parsedUrl.searchParams.get('from');
      const toFilter = parsedUrl.searchParams.get('to');
      const page = Math.max(1, parseInt(parsedUrl.searchParams.get('page') || '1', 10) || 1);
      const pageSize = Math.max(1, parseInt(parsedUrl.searchParams.get('page_size') || '20', 10) || 20);

      let filtered = [...this.auditLogEntries];

      if (typeFilter) {
        filtered = filtered.filter(e => e.type === typeFilter);
      }

      if (searchFilter) {
        const term = searchFilter.toLowerCase();
        filtered = filtered.filter(e =>
          e.type.toLowerCase().includes(term) ||
          e.source.toLowerCase().includes(term) ||
          (e.subject && e.subject.toLowerCase().includes(term))
        );
      }

      if (fromFilter) {
        const fromTime = new Date(fromFilter).getTime();
        if (!isNaN(fromTime)) {
          filtered = filtered.filter(e => new Date(e.time).getTime() >= fromTime);
        }
      }

      if (toFilter) {
        const toTime = new Date(toFilter).getTime();
        if (!isNaN(toTime)) {
          filtered = filtered.filter(e => new Date(e.time).getTime() <= toTime);
        }
      }

      filtered.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());

      const total = filtered.length;
      const totalPages = Math.max(1, Math.ceil(total / pageSize));
      const start = (page - 1) * pageSize;
      const items = filtered.slice(start, start + pageSize);

      return this.ok({
        items,
        pagination: {
          page,
          pageSize,
          total,
          totalPages,
          hasMore: page < totalPages
        }
      });
    }

    // Email Settings
    if (url.endsWith('/settings/email') && method === 'GET') {
      return this.ok({ email: this.emailSettings });
    }

    if (url.endsWith('/settings/email') && method === 'PUT') {
      const incoming = body?.email || {};
      this.emailSettings = { ...this.emailSettings, ...incoming };
      return this.ok({ email: this.emailSettings });
    }

    // Email Recipient Rules
    if (url.endsWith('/email-recipient-rules') && method === 'GET') {
      return this.ok({
        items: this.emailRecipientRules,
        pagination: {
          page: 1,
          pageSize: this.emailRecipientRules.length,
          total: this.emailRecipientRules.length,
          totalPages: 1,
          hasMore: false,
        },
      });
    }

    if (url.endsWith('/email-recipient-rules') && method === 'POST') {
      const rule = {
        id: `demo-rule-${this.nextRuleId++}`,
        rule_type: body.rule_type,
        pattern: body.pattern,
        description: body.description || undefined,
      };
      this.emailRecipientRules.push(rule);
      return this.ok(rule);
    }

    if (url.match(/\/email-recipient-rules\/[^/]+$/) && method === 'DELETE') {
      const ruleId = url.split('/').pop()!;
      const idx = this.emailRecipientRules.findIndex(r => r.id === ruleId);
      if (idx >= 0) {
        this.emailRecipientRules.splice(idx, 1);
        return this.ok({ deleted: true });
      }
      return this.notFound('Rule not found');
    }

    // Tenant Settings Mocks
    if (url.endsWith('/settings/tenant') && method === 'GET') {
      return this.ok(this.demoTenant.settings);
    }

    if (url.endsWith('/settings/tenant') && method === 'PUT') {
      this.demoTenant.settings = {
        ...this.demoTenant.settings,
        ...(body as any)
      };
      return this.ok(this.demoTenant.settings);
    }

    // Webhook Event Types catalog
    if (url.endsWith('/webhook-event-types') && method === 'GET') {
      const DOMAIN_NAMES: Record<string, string> = {
        tenant: 'Tenant Management', identity: 'Identity & Access',
        workspace: 'Workspace Management', connection: 'Connection Management',
        reconciliation: 'Reconciliation', platform: 'Platform Operations', webhook: 'Webhooks',
      };
      const allTypes = [
        'io.datarecs.tenant.tenant.provisioned', 'io.datarecs.tenant.domain.checked', 'io.datarecs.tenant.admin.notified',
        'io.datarecs.identity.user.authenticated', 'io.datarecs.identity.session.refreshed', 'io.datarecs.identity.user.logged_out',
        'io.datarecs.identity.user.invited', 'io.datarecs.identity.invitation.cancelled',
        'io.datarecs.workspace.workspace.created', 'io.datarecs.workspace.workspace.updated', 'io.datarecs.workspace.workspace.deleted',
        'io.datarecs.connection.connection.created', 'io.datarecs.connection.connection.updated',
        'io.datarecs.connection.connection.deleted', 'io.datarecs.connection.connection.tested',
        'io.datarecs.reconciliation.job.created', 'io.datarecs.reconciliation.job.updated',
        'io.datarecs.reconciliation.job.triggered', 'io.datarecs.reconciliation.run.started',
        'io.datarecs.reconciliation.run.stage_completed', 'io.datarecs.reconciliation.run.completed',
        'io.datarecs.reconciliation.run.failed', 'io.datarecs.reconciliation.run.cancelled',
        'io.datarecs.platform.api_key.created', 'io.datarecs.platform.api_key.updated', 'io.datarecs.platform.api_key.deleted',
        'io.datarecs.webhook.endpoint.created', 'io.datarecs.webhook.endpoint.updated',
        'io.datarecs.webhook.subscription.created', 'io.datarecs.webhook.delivery.failed',
      ];
      const domains: Record<string, { name: string; event_types: any[] }> = {};
      for (const type of allTypes) {
        const parts = type.split('.');
        if (parts.length < 5) continue;
        const [, , domain, entity, action] = parts;
        if (!domains[domain]) domains[domain] = { name: DOMAIN_NAMES[domain] || domain, event_types: [] };
        const name = `${entity}.${action}`.split(/[_.]/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        domains[domain].event_types.push({ type, domain, entity, action, name });
      }
      return this.ok({ domains, total: allTypes.length });
    }

    // Webhook Endpoints CRUD
    if (url.endsWith('/webhook-endpoints') && method === 'GET') {
      return this.ok({
        items: this.webhookEndpoints,
        pagination: this.buildPagination(this.webhookEndpoints.length)
      });
    }

    if (url.endsWith('/webhook-endpoints') && method === 'POST') {
      const endpoint = {
        ...body,
        id: this.generateId('ep'),
        enabled: true,
        rate_limit_rps: body?.rate_limit_rps ?? 50,
        signing_secret_rotated_at: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      this.webhookEndpoints.unshift(endpoint);
      return this.ok({ endpoint, signing_secret: 'whsec_demo_' + Math.random().toString(36).substring(2, 18) });
    }

    const epRotateMatch = url.match(/\/webhook-endpoints\/([^\/]+)\/rotate-secret$/);
    if (epRotateMatch && method === 'POST') {
      const epId = decodeURIComponent(epRotateMatch[1]);
      const ep = this.webhookEndpoints.find(e => e.id === epId);
      if (ep) {
        ep.signing_secret_rotated_at = new Date().toISOString();
        ep.updated_at = new Date().toISOString();
      }
      return this.ok({ signing_secret: 'whsec_demo_' + Math.random().toString(36).substring(2, 18) });
    }

    if (url.endsWith('/webhook-endpoints/preview') && method === 'POST') {
      return this.ok(this.previewWebhookTemplate(body));
    }

    const epPreviewMatch = url.match(/\/webhook-endpoints\/([^\/]+)\/preview$/);
    if (epPreviewMatch && method === 'POST') {
      return this.ok(this.previewWebhookTemplate(body));
    }

    const epVariablesMatch = url.match(/\/webhook-endpoints\/([^\/]+)\/available-variables$/);
    if (epVariablesMatch && method === 'GET') {
      return this.ok({
        variables: [
          { path: 'event.id', example: 'evt-demo-001' },
          { path: 'event.type', example: 'io.datarecs.reconciliation.run.completed' },
          { path: 'data.job.id', example: 'job-l1-orders-pg-mssql' },
          { path: 'data.run.status', example: 'COMPLETED' }
        ]
      });
    }

    const epSecretsMatch = url.match(/\/webhook-endpoints\/([^\/]+)\/secrets$/);
    if (epSecretsMatch && method === 'GET') {
      const epId = decodeURIComponent(epSecretsMatch[1]);
      return this.ok({ secrets: this.webhookSecrets.get(epId) || [] });
    }

    const epSecretNameMatch = url.match(/\/webhook-endpoints\/([^\/]+)\/secrets\/([^\/]+)$/);
    if (epSecretNameMatch && method === 'PUT') {
      const epId = decodeURIComponent(epSecretNameMatch[1]);
      const name = decodeURIComponent(epSecretNameMatch[2]);
      const existing = this.webhookSecrets.get(epId) || [];
      const next = existing.filter(secret => secret.name !== name);
      next.push({ name, setAt: new Date().toISOString() });
      this.webhookSecrets.set(epId, next);
      return this.ok({ secrets: next });
    }

    if (epSecretNameMatch && method === 'DELETE') {
      const epId = decodeURIComponent(epSecretNameMatch[1]);
      const name = decodeURIComponent(epSecretNameMatch[2]);
      const next = (this.webhookSecrets.get(epId) || []).filter(secret => secret.name !== name);
      this.webhookSecrets.set(epId, next);
      return this.ok({ secrets: next });
    }

    if (url.includes('/webhook-test') && method === 'POST') {
      return this.ok({ success: true, event_id: this.generateId('evt') });
    }

    const epDetailMatch = url.match(/\/webhook-endpoints\/([^\/]+)$/);
    if (epDetailMatch && method === 'GET') {
      const epId = decodeURIComponent(epDetailMatch[1]);
      const endpoint = this.webhookEndpoints.find(e => e.id === epId);
      return this.ok(endpoint || {});
    }

    // Real core-api uses PUT for endpoint updates (re-enable, toggle, edit); accept PATCH too for
    // backwards-compat with older demo callers. Return the endpoint object directly — the console's
    // WebhookEndpointsService.update() types the response as a WebhookEndpoint.
    if (epDetailMatch && (method === 'PUT' || method === 'PATCH')) {
      const epId = decodeURIComponent(epDetailMatch[1]);
      const endpoint = this.webhookEndpoints.find(e => e.id === epId);
      if (endpoint) {
        Object.assign(endpoint, body, { updated_at: new Date().toISOString() });
      }
      return this.ok(endpoint || {});
    }

    if (epDetailMatch && method === 'DELETE') {
      const epId = decodeURIComponent(epDetailMatch[1]);
      const idx = this.webhookEndpoints.findIndex(e => e.id === epId);
      if (idx >= 0) this.webhookEndpoints.splice(idx, 1);
      // Cascade-delete subscriptions
      const subIdxs: number[] = [];
      this.webhookSubscriptions.forEach((s, i) => { if (s.endpoint_id === epId) subIdxs.push(i); });
      for (let i = subIdxs.length - 1; i >= 0; i--) this.webhookSubscriptions.splice(subIdxs[i], 1);
      return this.ok({});
    }

    // ── Webhook Deliveries (delivery log, attempt history, retry, bulk replay) ──
    if (url.endsWith('/webhook-deliveries/replay') && method === 'POST') {
      // Bulk-replay matching deliveries: AND-combine the optional selectors, reset each match to
      // PENDING (re-attempt now), and report how many were re-enqueued.
      const epId = body?.endpoint_id as string | undefined;
      const status = body?.status as string | undefined;
      const matches = this.webhookDeliveries.filter(d =>
        (!epId || d.endpoint_id === epId) && (!status || d.status === status));
      const ts = new Date().toISOString();
      for (const d of matches) {
        d.status = 'PENDING';
        d.last_error = null;
        d.next_attempt_at = ts;
        d.updated_at = ts;
      }
      return this.ok({ replayed_count: matches.length });
    }

    const wdRetryMatch = url.match(/\/webhook-deliveries\/([^\/]+)\/retry$/);
    if (wdRetryMatch && method === 'POST') {
      const id = decodeURIComponent(wdRetryMatch[1]);
      const delivery = this.webhookDeliveries.find(d => d.id === id);
      if (delivery) {
        delivery.status = 'PENDING';
        delivery.last_error = null;
        delivery.next_attempt_at = new Date().toISOString();
        delivery.updated_at = new Date().toISOString();
      }
      return this.ok(delivery || {});
    }

    const wdDetailMatch = url.match(/\/webhook-deliveries\/([^\/]+)$/);
    if (wdDetailMatch && method === 'GET') {
      const id = decodeURIComponent(wdDetailMatch[1]);
      const delivery = this.webhookDeliveries.find(d => d.id === id);
      const attempts = this.webhookDeliveryAttempts[id] ?? [];
      return this.ok({ delivery: delivery || {}, attempts });
    }

    if (url.includes('/webhook-deliveries') && method === 'GET') {
      const params = new URL(urlWithParams, 'http://localhost').searchParams;
      const epId = params.get('endpoint_id');
      const status = params.get('status');
      const items = this.webhookDeliveries
        .filter(d => (!epId || d.endpoint_id === epId) && (!status || d.status === status))
        .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
      return this.ok({ items, pagination: this.buildPagination(items.length) });
    }

    // Webhook Subscriptions CRUD
    if (url.endsWith('/webhook-subscriptions') && method === 'GET') {
      const epFilter = new URL(urlWithParams, 'http://localhost').searchParams.get('endpoint_id');
      const items = epFilter
        ? this.webhookSubscriptions.filter(s => s.endpoint_id === epFilter)
        : this.webhookSubscriptions;
      return this.ok({ items, pagination: this.buildPagination(items.length) });
    }

    if (url.endsWith('/webhook-subscriptions') && method === 'POST') {
      const subscription = {
        ...body,
        id: this.generateId('sub'),
        enabled: true,
        filters: body?.filters || {},
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      this.webhookSubscriptions.unshift(subscription);
      return this.ok({ subscription });
    }

    const subDetailMatch = url.match(/\/webhook-subscriptions\/([^\/]+)$/);
    if (subDetailMatch && method === 'GET') {
      const subId = decodeURIComponent(subDetailMatch[1]);
      const subscription = this.webhookSubscriptions.find(s => s.id === subId);
      return this.ok({ subscription: subscription || {} });
    }

    if (subDetailMatch && method === 'PATCH') {
      const subId = decodeURIComponent(subDetailMatch[1]);
      const subscription = this.webhookSubscriptions.find(s => s.id === subId);
      if (subscription) {
        Object.assign(subscription, body, { updated_at: new Date().toISOString() });
      }
      return this.ok({ subscription: subscription || {} });
    }

    if (subDetailMatch && method === 'DELETE') {
      const subId = decodeURIComponent(subDetailMatch[1]);
      const idx = this.webhookSubscriptions.findIndex(s => s.id === subId);
      if (idx >= 0) this.webhookSubscriptions.splice(idx, 1);
      return this.ok({});
    }

    // ── OIDC Connectors ────────────────────────────────────────────────
    if (url.endsWith('/connectors/oidc') && method === 'GET') {
      return this.ok({ items: this.oidcConnectors, pagination: { total: this.oidcConnectors.length } });
    }

    if (url.endsWith('/connectors/oidc/test') && method === 'POST') {
      return this.ok({ success: true });
    }

    if (url.endsWith('/connectors/oidc') && method === 'POST') {
      const connector = {
        ...body,
        id: this.generateId('oidc'),
        created_at: new Date().toISOString(),
      };
      this.oidcConnectors.push(connector);
      return this.ok(connector);
    }

    const oidcDetailMatch = url.match(/\/connectors\/oidc\/([^\/]+)$/);
    if (oidcDetailMatch && method === 'GET') {
      const connectorId = decodeURIComponent(oidcDetailMatch[1]);
      const connector = this.oidcConnectors.find(c => c.id === connectorId);
      return connector ? this.ok(connector) : this.notFound({ message: 'OIDC connector not found' });
    }

    if (oidcDetailMatch && method === 'DELETE') {
      const connectorId = decodeURIComponent(oidcDetailMatch[1]);
      const idx = this.oidcConnectors.findIndex(c => c.id === connectorId);
      if (idx >= 0) this.oidcConnectors.splice(idx, 1);
      return this.ok({});
    }

    if (pathname.endsWith('/support/conversations') && method === 'GET') {
      const params = new URL(urlWithParams, 'http://localhost').searchParams;
      const status = params.get('status');
      const conversations = status
        ? this.supportConversations.filter(conversation => conversation.status === status)
        : this.supportConversations;
      const items = conversations.map(c => this.toSupportConversationDto(c));
      return this.ok({ items, pagination: this.buildPagination(items.length) });
    }

    if (pathname.endsWith('/support/conversations') && method === 'POST') {
      const now = new Date().toISOString();
      const conversation: DemoSupportConversation = {
        id: this.generateId('support'),
        subject: body?.subject || 'Demo support request',
        status: 'open',
        created_at: now,
        // The customer is opening this conversation right now — nothing unread yet.
        contactLastViewedAt: now
      };
      const message: DemoSupportMessage = {
        id: this.generateId('support-msg'),
        conversation_id: conversation.id,
        // The real CreateSupportConversationRequest field is `initial_message` (see
        // data-models-core-api/src/api-bodies/support/conversation.ts) — `body`/`message`
        // were a pre-existing demo-fixture mismatch that silently created every new demo
        // conversation with an empty first message.
        body: body?.initial_message || body?.body || body?.message || '',
        author_type: 'contact',
        author_name: 'Niamh Demo',
        created_at: now
      };
      this.supportConversations.unshift(conversation);
      this.supportMessages.set(conversation.id, [message]);
      this.scheduleDemoSupportAutoReply(conversation.id, now);
      return this.ok({ conversation: this.toSupportConversationDto(conversation), message: this.toSupportMessageDto(message) });
    }

    const supportMessagesMatch = url.match(/\/support\/conversations\/([^\/]+)\/messages$/);
    if (supportMessagesMatch && method === 'GET') {
      const conversationId = decodeURIComponent(supportMessagesMatch[1]);
      const items = this.visibleSupportMessages(conversationId);
      return this.ok({ items, pagination: this.buildPagination(items.length) });
    }

    if (supportMessagesMatch && method === 'POST') {
      const conversationId = decodeURIComponent(supportMessagesMatch[1]);
      const now = new Date().toISOString();
      const message: DemoSupportMessage = {
        id: this.generateId('support-msg'),
        conversation_id: conversationId,
        body: body?.body || '',
        author_type: 'contact',
        author_name: 'Niamh Demo',
        created_at: now
      };
      const messages = this.supportMessages.get(conversationId) || [];
      this.supportMessages.set(conversationId, [...messages, message]);
      // Schedule a canned agent auto-reply so the thread's 30s poll (and the sidebar's
      // 60s unread poll) have something real to pick up — the reply-loop UX has nothing
      // to prove in demo mode otherwise, since there is no simulated support agent.
      this.scheduleDemoSupportAutoReply(conversationId, now);
      return this.ok({ message: this.toSupportMessageDto(message) });
    }

    const supportDetailMatch = url.match(/\/support\/conversations\/([^\/]+)$/);
    if (supportDetailMatch && method === 'GET') {
      const conversationId = decodeURIComponent(supportDetailMatch[1]);
      const conversation = this.supportConversations.find(item => item.id === conversationId);
      if (!conversation) return this.notFound({ message: 'Support conversation not found' });
      const messages = this.visibleSupportMessages(conversationId);
      // Viewing the conversation detail is the demo's stand-in for Chatwoot's
      // "contact viewed" mark-read — clears the badge for this conversation. Recorded
      // AFTER reading `messages` above, so this response reflects what the user is
      // actually about to see, and the DTO below (computed fresh) reports unread_count 0.
      conversation.contactLastViewedAt = new Date().toISOString();
      return this.ok({
        conversation: this.toSupportConversationDto(conversation),
        messages: { items: messages, pagination: this.buildPagination(messages.length) }
      });
    }

    return null;
  }

  private getJobGroups(): any[] {
    return this.jobGroups.map(group => {
      const jobs = (group.job_ids || [])
        .map((jobId: string) => this.getJobById(jobId))
        .filter(Boolean);
      return {
        ...group,
        job_ids: group.job_ids || jobs.map((job: any) => job.id),
        jobs: jobs.length ? jobs : this.jobs.filter(job => job.parent_job_group_id === group.id)
      };
    });
  }

  private attachJobToGroup(jobGroupId: string, job: any): void {
    const group = this.jobGroups.find(g => g.id === jobGroupId);
    if (!group) {
      return;
    }
    group.jobs = group.jobs || [];
    group.job_ids = group.job_ids || [];
    if (!group.jobs.find((existing: any) => existing.id === job.id)) {
      group.jobs.push(job);
    }
    if (!group.job_ids.includes(job.id)) {
      group.job_ids.push(job.id);
    }
  }

  private getJobById(jobId: string): any | undefined {
    return this.jobs.find(job => job.id === jobId);
  }

  private getJobRuns(jobId: string): DemoJobRun[] {
    return this.jobRuns.get(jobId) || [];
  }

  private getAllRuns(): DemoJobRun[] {
    return Array.from(this.jobRuns.values()).flat();
  }

  private findRun(jobId: string, runId: string): DemoJobRun | undefined {
    return this.getJobRuns(jobId).find(run => run.id === runId);
  }

  private buildRunResultsSummary(jobId: string, runId: string): any {
    const run = this.findRun(jobId, runId);
    if (!run || !['COMPLETED', 'ERRORED'].includes(run.status)) {
      return { run_id: runId, source: 'run_aggregates', stages: [] };
    }

    const unmatched = run.status === 'ERRORED' ? Math.max(run.rows_unmatched || 0, 24) : run.rows_unmatched || 0;
    return {
      run_id: runId,
      job_id: jobId,
      source: 'stage_artifacts',
      stages: [
        {
          stage_index: 0,
          result: unmatched > 0 ? 'UNMATCHED' : 'MATCHED',
          rows_processed: run.rows_processed || 13200,
          rows_matched: Math.max((run.rows_processed || 13200) - unmatched, 0),
          rows_unmatched: unmatched,
          sample_available: true,
          sample_kind: unmatched > 0 ? 'unmatched' : 'matched_preview',
          sample_row_count: Math.min(unmatched > 0 ? unmatched : 25, 25),
          measure_exceed_counts: [
            { measure: 'total_amount', exceed_count: unmatched > 0 ? Math.min(unmatched, 18) : 0 }
          ]
        }
      ]
    };
  }

  private buildMismatchRows(params: URLSearchParams): any {
    const page = Math.max(1, Number(params.get('page') || '1') || 1);
    const pageSize = Math.max(1, Number(params.get('pageSize') || '25') || 25);
    const total = 42;
    const start = (page - 1) * pageSize;
    const rows = Array.from({ length: Math.max(0, Math.min(pageSize, total - start)) }, (_, index) => {
      const rowNumber = start + index + 1;
      const sourceA = 1200 + rowNumber * 4;
      const sourceB = sourceA - (rowNumber % 5) - 1;
      return {
        order_id: `ORD-${String(1000 + rowNumber).padStart(4, '0')}`,
        source_1_total_amount: sourceA,
        source_2_total_amount: sourceB,
        total_amount_delta: sourceA - sourceB,
        total_amount_pct_diff: Number((((sourceA - sourceB) / sourceA) * 100).toFixed(2)),
        total_amount_threshold_flag: true
      };
    });

    return {
      state: 'available',
      columns: [
        { name: 'order_id', role: 'dimension' },
        { name: 'source_1_total_amount', role: 'source_value', measure: 'total_amount', source_index: 0 },
        { name: 'source_2_total_amount', role: 'source_value', measure: 'total_amount', source_index: 1 },
        { name: 'total_amount_delta', role: 'delta', measure: 'total_amount' },
        { name: 'total_amount_pct_diff', role: 'percentage_difference', measure: 'total_amount' },
        { name: 'total_amount_threshold_flag', role: 'threshold_flag', measure: 'total_amount' }
      ],
      items: rows,
      pagination: {
        page,
        pageSize,
        total,
        totalPages: Math.ceil(total / pageSize),
        hasMore: page * pageSize < total
      },
      sample_size: total,
      sample_bounded: true
    };
  }

  private simulateRunLifecycle(jobId: string, run: DemoJobRun): void {
    setTimeout(() => {
      if (run.status !== 'QUEUED') {
        return;
      }
      run.status = 'RUNNING';
      run.started_at = new Date().toISOString();
      this.jobRunEvents.get(run.id)?.push(this.buildEvent(run, 'JOB_STARTED', { timestamp: run.started_at }));
      this.jobRuns.set(jobId, this.getJobRuns(jobId));
    }, 800);

    setTimeout(() => {
      if (run.status !== 'RUNNING') {
        return;
      }
      run.status = 'COMPLETED';
      run.completed_at = new Date().toISOString();
      run.rows_processed = 13200;
      run.rows_matched = 13040;
      run.rows_unmatched = 160;
      const events = this.jobRunEvents.get(run.id) || [];
      events.push(this.buildEvent(run, 'ROW_PROGRESS', {
        rows_processed: run.rows_processed,
        rows_matched: run.rows_matched,
        rows_unmatched: run.rows_unmatched
      }));
      events.push(this.buildEvent(run, 'JOB_COMPLETED', { completed_at: run.completed_at }));
      this.jobRunEvents.set(run.id, events);
      this.jobRuns.set(jobId, this.getJobRuns(jobId));
    }, 4000);
  }

  private buildWorkspaceResponse(): any {
    return {
      items: this.workspaces,
      pagination: this.buildPagination(this.workspaces.length),
      isAllWorkspaces: false,
      // The dashboard reads its summary stats from `data` on this same response
      // (ApiResponse<DashboardStats> shape) — the workspace pickers read `items`.
      data: this.buildDashboardStats()
    };
  }

  private buildInitialGroups(): DemoGroup[] {
    return [
      {
        id: 'group-security',
        name: 'security',
        description: 'developers focused on platform security',
        created_at: daysAgoIso(14),
        updated_at: new Date().toISOString()
      }
    ];
  }

  private buildInitialGroupMembers(): Map<string, DemoGroupMember[]> {
    const members = new Map<string, DemoGroupMember[]>();
    members.set('group-security', this.buildGroupMembers('group-security', ['user-kate', 'user-brent']));
    return members;
  }

  private buildGroupMembers(groupId: string, userIds: string[]): DemoGroupMember[] {
    return userIds
      .map(userId => this.findUser(userId))
      .filter((user): user is DemoUser => Boolean(user))
      .map(user => ({
        user_id: user.id,
        group_id: groupId,
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        joined_at: daysAgoIso(7)
      }));
  }

  private buildJoinRequests(): DemoJoinRequest[] {
    return [
      { id: 'join-security-ops', email: 'security.contractor@datarecs.io', created_at: daysAgoIso(1), status: 'pending' }
    ];
  }

  private getGroupMembersList(groupId: string): DemoGroupMember[] {
    return this.groupMembers.get(groupId) || [];
  }

  private addGroupMember(groupId: string, userId: string): DemoGroupMember | null {
    const user = this.findUser(userId);
    if (!user) {
      return null;
    }
    const existingMembers = this.getGroupMembersList(groupId);
    const existing = existingMembers.find(member => member.user_id === userId);
    if (existing) {
      return existing;
    }
    const newMember: DemoGroupMember = {
      user_id: user.id,
      group_id: groupId,
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      joined_at: new Date().toISOString()
    };
    this.groupMembers.set(groupId, [...existingMembers, newMember]);
    return newMember;
  }

  private removeGroupMember(groupId: string, userId: string): void {
    const members = this.getGroupMembersList(groupId);
    this.groupMembers.set(groupId, members.filter(member => member.user_id !== userId));
  }

  private findGroup(groupId: string): DemoGroup | undefined {
    return this.groups.find(group => group.id === groupId);
  }

  private findUser(userId: string): DemoUser | undefined {
    return this.users.find(user => user.id === userId);
  }

  private removeUser(userId: string): DemoUser | undefined {
    const idx = this.users.findIndex(user => user.id === userId);
    if (idx === -1) {
      return undefined;
    }
    const [removed] = this.users.splice(idx, 1);
    this.groupMembers.forEach((members, groupId) => {
      const filtered = members.filter(member => member.user_id !== userId);
      this.groupMembers.set(groupId, filtered);
    });
    const invitationIdx = this.invitations.findIndex(invite => invite.user_id === userId);
    if (invitationIdx >= 0) {
      const [invitation] = this.invitations.splice(invitationIdx, 1);
      this.clearInvitationTimer(invitation.id);
    }
    return removed;
  }

  private deriveNameFromEmail(email: string): { first_name: string; last_name: string } {
    if (!email) {
      return { first_name: 'Pending', last_name: 'User' };
    }
    const localPart = email.split('@')[0] || '';
    const segments = localPart.split(/[._-]/).filter(Boolean);
    const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);
    if (!segments.length) {
      return { first_name: 'Pending', last_name: 'User' };
    }
    return {
      first_name: capitalize(segments[0]),
      last_name: capitalize(segments[segments.length - 1] || 'User')
    };
  }

  private createInvitation(email: string, roles?: string[]): DemoInvitation {
    const trimmedEmail = (email || '').trim().toLowerCase();
    const userId = this.generateId('user');
    const { first_name, last_name } = this.deriveNameFromEmail(trimmedEmail);
    const invitation: DemoInvitation = {
      id: this.generateId('invite'),
      email: trimmedEmail,
      created_at: new Date().toISOString(),
      status: 'pending',
      user_id: userId,
      auto_accept_at: new Date(Date.now() + INVITE_AUTO_ACCEPT_MS).toISOString()
    };
    const newUser: DemoUser = {
      id: userId,
      email: trimmedEmail,
      first_name,
      last_name,
      roles: roles && roles.length ? roles : ['viewer'],
      permissions: VIEWER_PERMISSION_NAMES,
      status: 'pending',
      invited_at: invitation.created_at
    };
    this.users.push(newUser);
    this.invitations.push(invitation);
    this.scheduleInvitationAutoAccept(invitation);
    return invitation;
  }

  private scheduleInvitationAutoAccept(invitation: DemoInvitation): void {
    const timer = setTimeout(() => this.completeInvitation(invitation.id), INVITE_AUTO_ACCEPT_MS);
    this.invitationTimers.set(invitation.id, timer);
  }

  private completeInvitation(invitationId: string): void {
    const invitationIndex = this.invitations.findIndex(invite => invite.id === invitationId);
    if (invitationIndex === -1) {
      return;
    }
    const invitation = this.invitations[invitationIndex];
    if (invitation.status !== 'pending') {
      return;
    }
    invitation.status = 'accepted';
    this.invitations.splice(invitationIndex, 1);
    const user = this.findUser(invitation.user_id);
    if (user) {
      user.status = 'active';
      user.last_login_at = new Date().toISOString();
      delete user.invited_at;
    }
    this.clearInvitationTimer(invitationId);
  }

  private cancelInvitation(invitationId: string): void {
    const idx = this.invitations.findIndex(invite => invite.id === invitationId);
    if (idx === -1) {
      return;
    }
    const [invitation] = this.invitations.splice(idx, 1);
    this.clearInvitationTimer(invitationId);
    const user = this.findUser(invitation.user_id);
    if (user && user.status === 'pending') {
      this.removeUser(user.id);
    }
  }

  private clearInvitationTimer(invitationId: string): void {
    const timer = this.invitationTimers.get(invitationId);
    if (timer) {
      clearTimeout(timer);
      this.invitationTimers.delete(invitationId);
    }
  }

  private serializeInvitation(invitation: DemoInvitation) {
    const autoAcceptAt = invitation.auto_accept_at ? new Date(invitation.auto_accept_at).getTime() : null;
    const remainingSeconds = autoAcceptAt ? Math.max(0, Math.round((autoAcceptAt - Date.now()) / 1000)) : null;
    return {
      ...invitation,
      auto_accept_in_seconds: remainingSeconds
    };
  }

  private buildUserPermissions(user: DemoUser): any[] {
    const userGroups = this.groups.filter(g => 
      this.getGroupMembersList(g.id).some(m => m.user_id === user.id)
    );
    const userGroupIds = new Set(userGroups.map(g => g.id));

    const effectivePermissions: any[] = [];

    this.roleAssignments.forEach((assignments, roleKey) => {
      let roleName = roleKey;
      let roleType = 'platform';
      let scope = 'tenant';
      
      const pRole = this.platformRoles.find(r => r.name === roleKey || r.id.toString() === roleKey);
      if (pRole) {
        roleType = 'platform';
        roleName = pRole.name;
        scope = pRole.scope;
      } else {
        const cRole = this.customRoles.find(r => r.id.toString() === roleKey || r.name === roleKey);
        if (cRole) {
          roleType = 'custom';
          roleName = cRole.name;
          scope = cRole.scope;
        }
      }

      for (const assignment of assignments) {
        if (assignment.subject_type === 'user' && assignment.subject === user.id) {
          effectivePermissions.push({
            role_name: this.formatPermissionLabel(roleName),
            role_type: roleType,
            scope: scope,
            subject_type: 'user'
          });
        } else if (assignment.subject_type === 'group' && userGroupIds.has(assignment.subject)) {
          effectivePermissions.push({
            role_name: this.formatPermissionLabel(roleName),
            role_type: roleType,
            scope: scope,
            subject_type: 'group'
          });
        }
      }
    });

    return effectivePermissions;
  }

  private formatPermissionLabel(value: string): string {
    return value
      .split(/[_\s]+/)
      .filter(Boolean)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  private computeUserStats() {
    const activeUsers = this.users.filter(user => user.status === 'active').length;
    return {
      totalUsers: this.users.length,
      activeUsers,
      pendingInvites: this.invitations.length,
      pendingRequests: this.joinRequests.length,
      mostRecentLogin: this.buildMostRecentLogin()
    };
  }

  private buildMostRecentLogin(): { name: string; lastLogin: string } | null {
    const usersWithLogin = this.users.filter(user => Boolean(user.last_login_at));
    if (!usersWithLogin.length) {
      return null;
    }
    const [latest] = [...usersWithLogin].sort((a, b) => {
      const aTime = a.last_login_at ? new Date(a.last_login_at).getTime() : 0;
      const bTime = b.last_login_at ? new Date(b.last_login_at).getTime() : 0;
      return bTime - aTime;
    });
    return {
      name: `${latest.first_name} ${latest.last_name}`.trim(),
      lastLogin: latest.last_login_at as string
    };
  }

  private approveJoinRequest(requestId: string): void {
    const idx = this.joinRequests.findIndex(request => request.id === requestId);
    if (idx === -1) {
      return;
    }
    const [request] = this.joinRequests.splice(idx, 1);
    request.status = 'approved';
    this.createInvitation(request.email);
  }

  private denyJoinRequest(requestId: string): void {
    const idx = this.joinRequests.findIndex(request => request.id === requestId);
    if (idx === -1) {
      return;
    }
    this.joinRequests.splice(idx, 1);
  }

  private buildConnectionResponse(): any {
    return {
      items: this.connections.map(c => c.summary),
      pagination: this.buildPagination(this.connections.length)
    };
  }

  private buildConnectionStats(): any {
    const summaries = this.connections.map(c => c.summary);
    return {
      total: summaries.length,
      active: summaries.filter(c => c.status === 'active').length,
      withErrors: summaries.filter(c => c.status === 'error').length
    };
  }

  private buildConnectionDetailStats(connectionId: string): any {
    const jobsUsingConnection = this.jobs.filter(job =>
      (job.connections || []).some((connection: any) => connection.connection_id === connectionId)
    );
    const runs = jobsUsingConnection.flatMap(job =>
      (this.jobRuns.get(job.id) || []).map(run => ({ job, run }))
    ).sort((a, b) => {
      const aTime = a.run.started_at || a.run.completed_at || '';
      const bTime = b.run.started_at || b.run.completed_at || '';
      return bTime.localeCompare(aTime);
    });
    const lastSuccessfulRun = runs.find(({ run }) => run.status === 'COMPLETED');

    const isStale = connectionId === this.DEMO_STALE_CONNECTION_ID && !this.recoveredStaleConnections.has(connectionId);
    const healthFields = this.buildConnectionHealthFields(connectionId, isStale);

    return {
      jobs_using: jobsUsingConnection.length,
      total_runs: runs.length,
      last_run_at: runs[0]?.run.started_at || runs[0]?.run.completed_at,
      last_successful_run_at: lastSuccessfulRun?.run.completed_at || lastSuccessfulRun?.run.started_at,
      recent_runs: runs.slice(0, 6).map(({ job, run }) => ({
        run_id: run.id,
        job_id: job.id,
        job_name: job.name,
        status: run.status,
        started_at: run.started_at || run.completed_at
      })),
      ...healthFields
    };
  }

  /**
   * Real recurring health-check signal for the connection-detail health strip (R6.4) and the
   * stale banner (connection-health-check). The stale demo connection reports a run of recent
   * failed probes (invalid credentials — a "won't fix itself" class) that tipped it into `stale`;
   * a recovered/healthy connection reports a clean run of reachable probes.
   */
  private buildConnectionHealthFields(connectionId: string, isStale: boolean): any {
    // Only the designated demo connection carries real health-check telemetry; every other
    // connection falls back to the honest run-history strip (R6.2), exactly as in production
    // before the scheduler has reached it.
    if (connectionId !== this.DEMO_STALE_CONNECTION_ID) {
      return {};
    }
    const now = Date.now();
    const TWELVE_HOURS = 12 * 60 * 60 * 1000;
    // Newest-first, matching the API contract (component reverses to oldest→newest for the strip).
    const recent_health_checks = Array.from({ length: 8 }, (_, i) => {
      const checkedAt = new Date(now - i * TWELVE_HOURS).toISOString();
      // Recovered → all reachable. Stale → the three most-recent probes failed on bad credentials.
      const reachable = isStale ? i >= 3 : true;
      return {
        checked_at: checkedAt,
        reachable,
        error_class: reachable ? null : 'invalid_credentials',
        latency_ms: reachable ? 40 + (i % 5) * 7 : null
      };
    });
    if (!isStale) {
      return { health_state: 'healthy', recent_health_checks };
    }
    return {
      health_state: 'stale',
      stale_since: new Date(now - 26 * 60 * 60 * 1000).toISOString(),
      stale_error_class: 'invalid_credentials',
      recent_health_checks
    };
  }

  /**
   * Dashboard summary stats (the dashboard reads these off the GET /workspaces response).
   * Derived from the seeded demo data so the real "welcome back" hero renders in demo mode.
   *
   * First-run simulation: setting localStorage 'dr:demo-first-run' = 'true' (as the J34+
   * Playwright journeys do via addInitScript) returns all-zero counts so the first-run
   * checklist renders — the demo interceptor handles GET /workspaces before the network,
   * so journeys can't override this response with page.route().
   */
  private buildDashboardStats(): any {
    const firstRun = typeof localStorage !== 'undefined' && localStorage.getItem('dr:demo-first-run') === 'true';
    if (firstRun) {
      return {
        connections: { total: 0, active: 0, byType: {} },
        jobGroups: { total: 0, active: 0, scheduled: 0 },
        recentResults: { success: 0, partial: 0, failed: 0 }
      };
    }
    const byType = this.connections.reduce<Record<string, number>>((acc, conn) => {
      const type = String(conn.summary.type || 'unknown').toLowerCase();
      acc[type] = (acc[type] ?? 0) + 1;
      return acc;
    }, {});

    let success = 0;
    let partial = 0;
    let failed = 0;

    for (const [, runs] of this.jobRuns) {
      for (const run of runs) {
        if (run.status === 'COMPLETED') {
          if ((run.rows_unmatched ?? 0) > 0) {
            partial++;
          } else {
            success++;
          }
        } else if (run.status === 'ERRORED') {
          failed++;
        }
      }
    }

    return {
      connections: {
        total: this.connections.length,
        active: this.connections.filter(conn => conn.summary.status === 'active').length,
        byType
      },
      jobGroups: {
        total: this.jobGroups.length,
        active: this.jobs.filter(job => job.status === 'RUNNING').length,
        scheduled: this.jobGroups.filter(group => group.schedule_enabled !== false).length
      },
      recentResults: { success, partial, failed }
    };
  }

  private buildJobStats(): any {
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    let runsLast7Days = 0;
    let failedLast7Days = 0;
    let errorsLast7Days = 0;

    for (const [, runs] of this.jobRuns) {
      for (const run of runs) {
        const runDate = run.completed_at ? new Date(run.completed_at).getTime() :
                        run.started_at ? new Date(run.started_at).getTime() : 0;
        if (runDate >= sevenDaysAgo) {
          runsLast7Days++;
          if (run.status === 'ERRORED') {
            failedLast7Days++;
            errorsLast7Days++;
          }
        }
      }
    }

    return {
      totalJobs: this.jobs.length,
      runsLast7Days,
      failureRateLast7Days: runsLast7Days > 0 ? Math.round((failedLast7Days / runsLast7Days) * 100) : 0,
      errorsLast7Days
    };
  }

  private buildConnectionSearchResponse(rawQuery: string, limit: number): any {
    const query = rawQuery.trim().toLowerCase();
    if (!query) {
      return { query: '', types: [], connections: [] };
    }

    const typeMeta: Record<string, { label: string; aliases: string[] }> = {
      [ConnectionType.POSTGRES]: { label: 'PostgreSQL', aliases: ['postgres', 'postgresql', 'psql'] },
      [ConnectionType.MYSQL]: { label: 'MySQL', aliases: ['mysql'] },
      [ConnectionType.SNOWFLAKE]: { label: 'Snowflake', aliases: ['snowflake', 'snowflake db', 'sf'] },
      [ConnectionType.MSSQL]: { label: 'SQL Server', aliases: ['mssql', 'sql server', 'sqlserver'] },
      [ConnectionType.DB2]: { label: 'DB2', aliases: ['db2', 'ibm db2'] },
      [ConnectionType.ORACLE]: { label: 'Oracle', aliases: ['oracle'] },
      [ConnectionType.BIGQUERY]: { label: 'BigQuery', aliases: ['bigquery', 'big query', 'bq'] },
      [ConnectionType.GOOGLE_SHEETS]: { label: 'Google Sheets', aliases: ['google sheets', 'sheets', 'gsheets'] },
      [ConnectionType.EXCEL_SHAREPOINT]: { label: 'Excel (SharePoint)', aliases: ['excel', 'sharepoint', 'excel sharepoint', 'microsoft excel'] },
      [ConnectionType.TABLEAU]: { label: 'Tableau', aliases: ['tableau'] },
      [ConnectionType.LOOKER]: { label: 'Looker', aliases: ['looker'] },
    };

    const scoreTerms = (terms: string[]): number => {
      let best = 0;
      for (const term of terms) {
        const candidate = String(term || '').toLowerCase();
        if (!candidate) continue;
        if (candidate === query) best = Math.max(best, 120);
        else if (candidate.startsWith(query)) best = Math.max(best, 100);
        else if (candidate.includes(` ${query}`)) best = Math.max(best, 82);
        else if (candidate.includes(query)) best = Math.max(best, 70);

        const compactQuery = query.replace(/[\s_-]+/g, '');
        const compactCandidate = candidate.replace(/[\s_-]+/g, '');
        if (compactQuery && compactCandidate.startsWith(compactQuery)) {
          best = Math.max(best, 92);
        } else if (compactQuery && compactCandidate.includes(compactQuery)) {
          best = Math.max(best, 68);
        }
      }
      return best;
    };

    const summaries = this.connections.map(c => c.summary);
    const typeCounts = new Map<string, number>();
    for (const connection of summaries) {
      typeCounts.set(connection.type, (typeCounts.get(connection.type) || 0) + 1);
    }

    const types = Array.from(typeCounts.entries())
      .map(([type, count]) => {
        const meta = typeMeta[type] || { label: type, aliases: [] };
        const score = scoreTerms([meta.label, type, ...meta.aliases]);
        if (score === 0) return null;
        return { score, item: { type, label: meta.label, count } };
      })
      .filter((entry): entry is { score: number; item: any } => entry !== null)
      .sort((a, b) => b.score - a.score || a.item.label.localeCompare(b.item.label))
      .slice(0, 5)
      .map(entry => entry.item);

    const connections = summaries
      .map((connection) => {
        const meta = typeMeta[connection.type] || { label: connection.type, aliases: [] };
        const score = scoreTerms([
          connection.name,
          connection.description || '',
          connection.type,
          meta.label,
          ...meta.aliases,
        ]);
        if (score === 0) return null;
        return {
          score,
          item: {
            id: connection.id,
            name: connection.name,
            description: connection.description,
            type: connection.type,
            status: connection.status,
            workspace_id: connection.workspace_id,
          }
        };
      })
      .filter((entry): entry is { score: number; item: any } => entry !== null)
      .sort((a, b) => b.score - a.score || a.item.name.localeCompare(b.item.name))
      .slice(0, Math.max(1, Math.min(limit, 25)))
      .map(entry => entry.item);

    return { query, types, connections };
  }

  private buildCommandPaletteSearchResponse(rawQuery: string, limit: number): any {
    const query = rawQuery.trim().toLowerCase();
    const cappedLimit = Math.max(1, Math.min(limit, 25));
    if (!query) {
      return {
        query: '',
        connectionTypes: [],
        connections: [],
        jobs: [],
        workspaces: [],
        users: [],
        groups: [],
        apiKeys: [],
        supportConversations: [],
      };
    }

    const scoreTerms = (terms: string[]): number => {
      let best = 0;
      for (const term of terms) {
        const candidate = String(term || '').toLowerCase();
        if (!candidate) continue;
        if (candidate === query) best = Math.max(best, 120);
        else if (candidate.startsWith(query)) best = Math.max(best, 100);
        else if (candidate.includes(` ${query}`)) best = Math.max(best, 82);
        else if (candidate.includes(query)) best = Math.max(best, 70);

        const compactQuery = query.replace(/[\s_-]+/g, '');
        const compactCandidate = candidate.replace(/[\s_-]+/g, '');
        if (compactQuery && compactCandidate.startsWith(compactQuery)) {
          best = Math.max(best, 92);
        } else if (compactQuery && compactCandidate.includes(compactQuery)) {
          best = Math.max(best, 68);
        }
      }
      return best;
    };

    const workspaceNameById = new Map(this.workspaces.map(workspace => [workspace.id, workspace.name]));
    const connectionSearch = this.buildConnectionSearchResponse(rawQuery, cappedLimit);

    const jobs = this.jobs
      .map((job) => {
        const workspaceName = workspaceNameById.get(job.workspace_id);
        const score = scoreTerms([
          job.name,
          job.description || '',
          workspaceName || '',
          'job',
          'reconciliation',
        ]);
        if (score === 0) return null;
        return {
          score,
          item: {
            id: job.id,
            name: job.name,
            description: job.description,
            workspace_id: job.workspace_id,
            workspace_name: workspaceName,
            last_run_status: job.last_run_status,
          }
        };
      })
      .filter((entry): entry is { score: number; item: any } => entry !== null)
      .sort((a, b) => b.score - a.score || a.item.name.localeCompare(b.item.name))
      .slice(0, cappedLimit)
      .map(entry => entry.item);

    const workspaces = this.workspaces
      .map((workspace) => {
        const score = scoreTerms([
          workspace.name,
          workspace.slug,
          workspace.description || '',
          'workspace',
        ]);
        if (score === 0) return null;
        return {
          score,
          item: {
            id: workspace.id,
            name: workspace.name,
            slug: workspace.slug,
            description: workspace.description,
          }
        };
      })
      .filter((entry): entry is { score: number; item: any } => entry !== null)
      .sort((a, b) => b.score - a.score || a.item.name.localeCompare(b.item.name))
      .slice(0, cappedLimit)
      .map(entry => entry.item);

    const users = this.users
      .map((user) => {
        const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
        const score = scoreTerms([
          user.email,
          user.first_name || '',
          user.last_name || '',
          fullName,
          'user',
        ]);
        if (score === 0) return null;
        return {
          score,
          item: {
            id: user.id,
            email: user.email,
            first_name: user.first_name,
            last_name: user.last_name,
            full_name: fullName || undefined,
          }
        };
      })
      .filter((entry): entry is { score: number; item: any } => entry !== null)
      .sort((a, b) => b.score - a.score || a.item.email.localeCompare(b.item.email))
      .slice(0, cappedLimit)
      .map(entry => entry.item);

    const groups = this.groups
      .map((group) => {
        const score = scoreTerms([
          group.name,
          group.description || '',
          'group',
          'team',
          'access',
        ]);
        if (score === 0) return null;
        return {
          score,
          item: {
            id: group.id,
            name: group.name,
            description: group.description,
          }
        };
      })
      .filter((entry): entry is { score: number; item: any } => entry !== null)
      .sort((a, b) => b.score - a.score || a.item.name.localeCompare(b.item.name))
      .slice(0, cappedLimit)
      .map(entry => entry.item);

    const apiKeys = this.apiKeys
      .map((apiKey) => {
        const score = scoreTerms([
          apiKey.name,
          apiKey.description || '',
          apiKey.prefix,
          'api key',
          'token',
        ]);
        if (score === 0) return null;
        return {
          score,
          item: {
            id: apiKey.id,
            name: apiKey.name,
            description: apiKey.description,
            prefix: apiKey.prefix,
          }
        };
      })
      .filter((entry): entry is { score: number; item: any } => entry !== null)
      .sort((a, b) => b.score - a.score || a.item.name.localeCompare(b.item.name))
      .slice(0, cappedLimit)
      .map(entry => entry.item);

    const supportConversations = this.supportConversations
      .map((conversation) => {
        const dto = this.toSupportConversationDto(conversation);
        const score = scoreTerms([
          dto.subject,
          dto.status,
          'support',
          'ticket',
          'conversation',
        ]);
        if (score === 0) return null;
        return {
          score,
          item: dto,
        };
      })
      .filter((entry): entry is { score: number; item: any } => entry !== null)
      .sort((a, b) => b.score - a.score || a.item.subject.localeCompare(b.item.subject))
      .slice(0, cappedLimit)
      .map(entry => entry.item);

    return {
      query,
      connectionTypes: connectionSearch.types,
      connections: connectionSearch.connections.map((connection: any) => ({
        ...connection,
        workspace_name: workspaceNameById.get(connection.workspace_id),
      })),
      jobs,
      workspaces,
      users,
      groups,
      apiKeys,
      supportConversations,
    };
  }

  private wrap<T>(data: T): ApiResponse<T> {
    return { data, status: 200, message: 'OK' } as ApiResponse<T>;
  }

  private findConnection(id: string): DemoConnectionDefinition | undefined {
    return this.connections.find(c => c.summary.id === decodeURIComponent(id));
  }

  private getDatabase(connectionId: string, databaseName: string): DemoDatabase | undefined {
    const conn = this.findConnection(connectionId);
    return conn?.metadata.find(db => db.name === decodeURIComponent(databaseName));
  }

  private getTable(connectionId: string, databaseName: string, schemaName: string, tableName: string): DemoTable | undefined {
    const database = this.getDatabase(connectionId, databaseName);
    const schema = database?.schemas.find(s => s.name === decodeURIComponent(schemaName));
    return schema?.tables.find(t => t.name === decodeURIComponent(tableName));
  }

  private buildPagination(count: number) {
    return {
      page: 1,
      pageSize: count,
      total: count,
      totalPages: 1,
      hasMore: false
    };
  }

  private removeById<T extends Record<string, any>>(items: T[], key: keyof T, value: string): void {
    const idx = items.findIndex(item => item[key] === value);
    if (idx >= 0) {
      items.splice(idx, 1);
    }
  }

  private slugify(value: string): string {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || this.generateId('workspace');
  }

  private ensureSsoConnection(): void {
    if (this.ssoConnection) {
      return;
    }
    this.ssoConnection = {
      id: 'sso-demo-connection',
      name: 'Demo SSO',
      status: 'active',
      provider: 'oidc',
      scim_enabled: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
  }

  private previewWebhookTemplate(body: any): any {
    return {
      body: body?.template || JSON.stringify({ event: 'io.datarecs.reconciliation.run.completed', runId: 'run-demo' }, null, 2),
      headers: body?.headers || { 'content-type': 'application/json' },
      contentType: body?.contentType || 'application/json'
    };
  }

  private generateId(prefix: string): string {
    return `${prefix}-${Math.random().toString(36).substring(2, 10)}`;
  }

  public updateKekOwnership(ownership: 'managed' | 'byok', details?: { residence: string; name: string }): void {
    this.kekData.ownership = ownership;
    if (details) {
      this.kekData.residence = details.residence;
      this.kekData.name = details.name;
    } else if (ownership === 'managed') {
      this.kekData.residence = 'GCP KMS eu-west-2';
      this.kekData.name = 'tenant-demo-tenant-0001';
    }
  }

  public updateStorageOwnership(
    ownership: 'managed' | 'byos',
    details?: { bucketName: string; gcpProject: string; region: string }
  ): void {
    this.storageData.ownership = ownership;
    if (details) {
      this.storageData.bucket_name = details.bucketName;
      this.storageData.gcp_project = details.gcpProject;
      this.storageData.region = details.region;
      this.storageData.service_account =
        `tenant-${this.demoTenant.id}@datarecs-demo.iam.gserviceaccount.com`;
    } else if (ownership === 'managed') {
      this.storageData.bucket_name = `datarecs-platform-${this.demoTenant.id}`;
      this.storageData.gcp_project = undefined;
      this.storageData.region = 'EU (multi-region)';
      this.storageData.service_account = undefined;
    }
  }

  private ok<T>(body: T): Observable<HttpEvent<T>> {
    return of(body).pipe(
      delay(this.latency),
      map(res => new HttpResponse({ status: 200, body: res }))
    );
  }

  // ── Demo audit-events fixtures ──────────────────────────────────────────────
  private auditEventsCache: AuditEventEntry[] | null = null;
  private auditExportsStore = new Map<string, { job: AuditExportJob; polls: number }>();

  /** A representative spread: chained+Ed25519, chained+HMAC, and a legacy (pre-chain) record. */
  private demoAuditEvents(): AuditEventEntry[] {
    if (this.auditEventsCache) {
      return this.auditEventsCache;
    }
    const now = Date.now();
    const at = (minsAgo: number) => new Date(now - minsAgo * 60_000).toISOString();
    const base = {
      schema_version: 1,
      tenant_id: 'a1b2c3d4e5f60718',
      changes: {},
      metadata: {},
      risk: {},
    };
    const events: AuditEventEntry[] = [
      {
        ...base, id: 'ae_0001', event_class: 'SECURITY', event_type: 'io.datarecs.iam.api_key.created',
        subject: 'api_key:key-ledger', resource_type: 'api_key', resource_id: 'key-ledger', action: 'create',
        outcome: 'SUCCESS', severity: 'NOTICE', occurred_at: at(4), recorded_at: at(4),
        actor: { type: 'user', id: 'usr_ava', display: 'Ava Okonkwo' }, authority: { type: 'session', id: 'sess_88' },
        request: { id: 'req_a1', trace_id: 'trc_a1', source_ip: '203.0.113.24', user_agent: 'Mozilla/5.0 Console' },
        payload_hash: 'sha256:9c1a…f0', sequence: 5012, previous_hash: 'sha256:aa11…', event_hash: 'sha256:bb22…',
        chain_id: 'chain-2026-07', signature_algorithm: 'TRANSIT-ED25519', signing_key_version: '4', signature: 'ed25519:MEUCIQ…',
      } as AuditEventEntry,
      {
        ...base, id: 'ae_0002', event_class: 'DATA_EGRESS', event_type: 'io.datarecs.results.export.downloaded',
        subject: 'run:run-j08lji6a', resource_type: 'job_run', resource_id: 'run-j08lji6a', action: 'download',
        outcome: 'SUCCESS', severity: 'INFO', occurred_at: at(19), recorded_at: at(19),
        actor: { type: 'user', id: 'usr_liam', display: 'Liam Petrov' }, authority: { type: 'session', id: 'sess_71' },
        request: { id: 'req_a2', trace_id: 'trc_a2', source_ip: '198.51.100.9' },
        payload_hash: 'sha256:71bd…c3', sequence: 5011, previous_hash: 'sha256:99aa…', event_hash: 'sha256:aa11…',
        chain_id: 'chain-2026-07', signature_algorithm: 'HMAC-SHA256', signature: 'hmac:3b9d…',
      } as AuditEventEntry,
      {
        ...base, id: 'ae_0003', event_class: 'SECURITY', event_type: 'io.datarecs.iam.login.denied',
        subject: 'user:usr_mallory', resource_type: 'session', resource_id: '—', action: 'login',
        outcome: 'DENIED', severity: 'WARNING', occurred_at: at(46), recorded_at: at(46),
        actor: { type: 'user', id: 'usr_mallory', display: 'Unknown (MFA failed)' }, authority: { type: 'idp', id: 'workos' },
        request: { id: 'req_a3', trace_id: 'trc_a3', source_ip: '192.0.2.66', user_agent: 'curl/8.4' },
        payload_hash: 'sha256:12ef…88', sequence: 5010, previous_hash: 'sha256:88cc…', event_hash: 'sha256:99aa…',
        chain_id: 'chain-2026-07', signature_algorithm: 'TRANSIT-ED25519', signing_key_version: '4', signature: 'ed25519:MEQCIH…',
      } as AuditEventEntry,
      {
        ...base, id: 'ae_0004', event_class: 'CONFIG', event_type: 'io.datarecs.connections.credential.rotated',
        subject: 'connection:conn-postgres-ecommerce', resource_type: 'connection', resource_id: 'conn-postgres-ecommerce', action: 'rotate',
        outcome: 'SUCCESS', severity: 'NOTICE', occurred_at: at(140), recorded_at: at(140),
        actor: { type: 'user', id: 'usr_ava', display: 'Ava Okonkwo' }, authority: { type: 'session', id: 'sess_60' },
        request: { id: 'req_a4', trace_id: 'trc_a4', source_ip: '203.0.113.24' },
        payload_hash: 'sha256:44dd…21', sequence: 5009, previous_hash: 'sha256:77bb…', event_hash: 'sha256:88cc…',
        chain_id: 'chain-2026-07', signature_algorithm: 'HMAC-SHA256', signature: 'hmac:7c1a…',
      } as AuditEventEntry,
      {
        // Legacy: pre-chain record — no sequence/event_hash/chain_id. Carries a payload hash only.
        ...base, id: 'ae_legacy_0007', event_class: 'ADMIN', event_type: 'io.datarecs.workspaces.member.removed',
        subject: 'user:usr_removed', resource_type: 'workspace', resource_id: 'ws_main', action: 'remove_member',
        outcome: 'SUCCESS', severity: 'INFO', occurred_at: at(4300), recorded_at: at(4300),
        actor: { type: 'user', id: 'usr_owner', display: 'Owner (legacy)' }, authority: { type: 'session' },
        request: { id: 'req_a7', trace_id: 'trc_a7' },
        payload_hash: 'sha256:0a0a…ee', signature: '',
      } as AuditEventEntry,
    ];
    this.auditEventsCache = events;
    return events;
  }

  private buildDemoAuditEventsResponse(req: HttpRequest<any>): { items: AuditEventEntry[]; pagination: { total: number; has_more: boolean; page_size: number; next_cursor?: string } } {
    const p = req.params;
    let items = this.demoAuditEvents();
    const eventClass = p.get('event_class');
    const outcome = p.get('outcome');
    const severity = p.get('severity');
    const eventType = p.get('event_type');
    const actorType = p.get('actor_type');
    const actorId = p.get('actor_id');
    const resourceType = p.get('resource_type');
    const resourceId = p.get('resource_id');
    const channel = p.get('channel');
    const from = p.get('from');
    const to = p.get('to');
    const search = p.get('search')?.toLowerCase();
    if (eventClass) items = items.filter(e => e.event_class === eventClass);
    if (outcome) items = items.filter(e => e.outcome === outcome);
    if (severity) items = items.filter(e => e.severity === severity);
    if (eventType) items = items.filter(e => e.event_type === eventType);
    if (actorType) items = items.filter(e => e.actor?.type?.toUpperCase() === actorType.toUpperCase());
    if (actorId) items = items.filter(e => e.actor?.id === actorId);
    if (resourceType) items = items.filter(e => e.resource_type === resourceType);
    if (resourceId) items = items.filter(e => e.resource_id === resourceId);
    if (channel) items = items.filter(e => (e as any).channel === channel);
    if (from) items = items.filter(e => new Date(e.occurred_at) >= new Date(from));
    if (to) items = items.filter(e => new Date(e.occurred_at) <= new Date(to));
    if (search) items = items.filter(e => JSON.stringify(e).toLowerCase().includes(search));
    return { items, pagination: { total: items.length, has_more: false, page_size: 25 } };
  }

  private createDemoAuditExport(body: { format?: 'ndjson' | 'csv' | 'ocsf-ndjson' }): AuditExportJob {
    const id = this.generateId('aexp');
    const job: AuditExportJob = {
      id,
      state: 'QUEUED',
      format: body.format ?? 'ndjson',
      created_at: new Date().toISOString(),
    };
    this.auditExportsStore.set(id, { job, polls: 0 });
    return job;
  }

  /** Each status poll advances the job: QUEUED → RUNNING → SUCCEEDED (with sha256 + signed manifest). */
  private advanceDemoAuditExport(id: string): AuditExportJob | null {
    const record = this.auditExportsStore.get(id);
    if (!record) {
      return null;
    }
    record.polls += 1;
    if (record.polls === 1) {
      record.job = { ...record.job, state: 'RUNNING' };
    } else if (record.polls >= 2 && record.job.state !== 'SUCCEEDED') {
      record.job = {
        ...record.job,
        state: 'SUCCEEDED',
        row_count: this.demoAuditEvents().length,
        sha256: 'sha256:3f9c0b7e5a1d4e2c8b6f0a9d7c3e1f5b2a4d6e8c0f1a3b5d7e9c1f3a5b7d9e0f',
        completed_at: new Date().toISOString(),
        expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        signed_manifest: { algorithm: 'TRANSIT-ED25519', key_version: '4', signature: 'ed25519:MANIFEST…' },
      };
    }
    this.auditExportsStore.set(id, record);
    return record.job;
  }

  private notFound<T>(body: T): Observable<HttpEvent<T>> {
    return of(body).pipe(
      delay(this.latency),
      map(res => new HttpResponse({ status: 404, body: res }))
    );
  }
}
